import type {RespondentQuestion} from '@digital-twins/shared-types';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  clearDependents,
  dependentsOfDelete,
  exposureBoundaryBeforeMove,
  exposureZonesOf,
  gridRowsRandomized,
  isQuestionDependencyAvailableAtExposure,
  nonKeyframeableVideoMessage,
  POINT_LABEL_RATING_UNIT_IDS,
  QUESTIONNAIRE_EXPOSURE_BOUNDARY_SCHEMA_VERSION,
  RATING_SCALE_POINTS,
  resolveExposureMarkIndexes,
} from "@digital-twins/shared-types";
import type {
  ChoiceOption,
  ChoicesFromRef,
  ConceptAsset,
  MatrixRatingMeasure,
  MatrixRow,
  QuestionDefinition,
  QuestionnaireDraft,
  QuestionnaireDraftReadiness,
  QuestionnaireExposureMark,
  QuestionnaireExposureMarkName,
  QuestionnaireHttpOperation,
  QuestionnaireOperationEffect,
  QuestionnaireValidateResponse,
  QuestionnaireValidationIssue,
  QuestionRoutingRule,
  QuestionVisibilityRuleExpr,
  ContinuousUnitId,
  RatingResponse,
  RatingUnitId,
  ResponseDefinition,
  StudyGoals,
  SurveyJson,
  TestedAssetBlock,
  TestedAssetType,
} from "@digital-twins/shared-types";
import {
  CHOICES_FROM_SOURCE_QUESTION_TYPES,
  MATRIX_QUESTION_TYPES,
  OPTION_LIST_QUESTION_TYPES,
  OPTION_ORDER_QUESTION_TYPES,
  SELECT_QUESTION_TYPES,
  buildAssetMentionBindingTable,
  carriedListKeepsComparison,
  getUnitRegistryEntry,
  isChoicesFromModeReachable,
  keyWithTail,
  mintAnswerValue,
  mintKey,
  questionRefLabel,
  collectedAssetContent,
  selectOptionsOrder,
  rankingCountsHeld,
  rankingMaxLength,
  selectionCountsHeld,
  ratingBounds,
  ratingMidpointLabel,
  slugKey,
  takenKeys,
  withCarriedChoices,
} from "@digital-twins/shared-types";
import { toast } from "sonner";


function continuousThresholdBounds(response: {
  min?: number;
  max?: number;
  unit?: ContinuousUnitId;
}): [number, number] | null {
  const authoredMin =
    typeof response.min === "number" && Number.isFinite(response.min)
      ? response.min
      : undefined;
  const authoredMax =
    typeof response.max === "number" && Number.isFinite(response.max)
      ? response.max
      : undefined;
  if (authoredMin !== undefined || authoredMax !== undefined) {
    return [
      authoredMin ?? Number.NEGATIVE_INFINITY,
      authoredMax ?? Number.POSITIVE_INFINITY,
    ];
  }
  if (!response.unit) {
    return null;
  }
  const entry = getUnitRegistryEntry(response.unit);
  if (
    !entry ||
    typeof entry.rawMin !== "number" ||
    typeof entry.rawMax !== "number"
  ) {
    return null;
  }
  return [entry.rawMin, entry.rawMax];
}

import i18n from "@/i18n";
import { uploadQuestionnaireAssetFileApi } from "../api/questionnaireDraftsApi";
import type { IssuedAssetFile } from "@/lib/api/client";

import {
  DEFAULT_RATING_TEMPLATE_KEY,
  ratingScaleTemplatesFor,
} from "../ratingScaleTemplates";
import { estimatedQuestionnaireMinutes } from "@digital-twins/shared-types";
import { QUESTIONNAIRE_RESEARCH_QUESTION_CEILING } from "../questionnaireLimits";
import {
  addConceptAsset as addConceptAssetToBlock,
  attachConceptImage as attachConceptImageToBlock,
  attachConceptUrl as attachConceptUrlToBlock,
  ensureConceptAssetBlock,
  getConceptAssets,
  mediaRefFromIssuedFile,
  mediaRefFromPdfPreview,
  mediaRefFromUpload,
  removeConceptAsset as removeConceptAssetFromBlock,
  upsertConceptAsset,
  type AssetMentionTables,
} from "../assets/conceptAssets";
import {
  getSaveFailureCopy,
  isQuestionnaireDraftConflict,
} from "../utils/compileErrorMessage";
import { assetUploadRefusalLine } from "../assets/assetMediaAccept";
import { exposureLinesAreOneStrip, exposureViewOf } from "../assets/exposureZone";
import {
  SAVE_TOAST_POSITION,
  announceSaveEffects,
  requestFailureLine,
  signInActionFor,
} from "../utils/questionnaireIssueCopy";
import { getValidationTarget } from "../draft/createLocalDraft";
import {
  dryRunIssuesByQuestionId,
  NO_DRY_RUN_ISSUES,
  type DryRunIssues,
} from "../utils/dryRunIssues";
import {
  diffDraftToOperations,
  draftContentFingerprint,
} from "../utils/draftOperations";
import {
  clearUnsavedDraft,
  readUnsavedDraft,
  type StoredSaveRefusal,
  writeUnsavedDraft,
} from "../utils/unsavedDraftStorage";


const NO_CONCEPTS_ANALYZING: Set<string> = new Set();

interface SelectedPopulation {
  id: string;
  name: string;
  personaCount?: number;
}

export type CanonicalEditableQuestion = QuestionDefinition;

export interface QuestionnaireQuestionRecord {
  id: string;
  index: number;
  question: CanonicalEditableQuestion;
  sectionLabel: string;
}

export interface QuestionnaireTypeOption {
  id: QuestionDefinition["type"];
  label: string;
  badgeLabel: string;
  tone: "green" | "coral" | "gray";
}

interface SaveDraftResult {
  revision?: number;
  readiness?: QuestionnaireDraftReadiness | null;
  draft: QuestionnaireDraft;
  surveyJson: SurveyJson | null;
  /** What the server cascaded to keep the saved draft's references whole. */
  effects?: Array<QuestionnaireOperationEffect>;
}

/** A Save that wrote: the accepted draft, as the server returned it. */
export type WorkspaceSaved = { status: "saved" } & SaveDraftResult;

/**
 * What one Save did (L3). Every exit is one of these, so a caller branches on
 * `status` and never guesses from a missing draft. `saveChanges` has already
 * told the user why a Save was refused or failed (its toast and, for a
 * refusal, the cards' chips); a caller only decides whether to go on.
 * - `nothing_to_save`: there is no draft or saved baseline to save against;
 *   `draft` is the saved draft (null on a study with none yet).
 * - `refused`: the server refused the batch; `issues` are on the cards.
 * - `failed`: the request failed or lost to a newer draft (409).
 * - `superseded`: the editor moved to another draft while it ran.
 */
export type WorkspaceSaveOutcome =
  | WorkspaceSaved
  | { status: "nothing_to_save"; draft: QuestionnaireDraft | null }
  | {
      status: "refused";
      issues: SaveRefusalIssues;
      title: string;
      description: string | null;
    }
  | { status: "failed"; title: string; description: string | null }
  | { status: "superseded" };

const SUPERSEDED: WorkspaceSaveOutcome = { status: "superseded" };

/** How long edits pause before the dry run of their Save is asked. */
const DRY_RUN_PAUSE_MS = 400;

/** Issues a refused Save left on the questions it names, shown while dirty. */
export interface SaveRefusalIssues {
  byQuestionId: Map<string, Array<QuestionnaireValidationIssue>>;
  /**
   * The refusals of an operation on a tested item, by the item's id, for its
   * card (QA4 N7: a repeated price was named on no field).
   */
  byAssetId: Map<string, Array<QuestionnaireValidationIssue>>;
  unscoped: Array<QuestionnaireValidationIssue>;
}

const NO_SAVE_REFUSAL: SaveRefusalIssues = {
  byQuestionId: new Map(),
  byAssetId: new Map(),
  unscoped: [],
};

const NO_REFUSAL_STATE: { fingerprint: string | null; issues: SaveRefusalIssues } = {
  fingerprint: null,
  issues: NO_SAVE_REFUSAL,
};

/** A refusal as tab storage keeps it (`StoredSaveRefusal`), or null. */
function refusalForStorage(refusal: {
  fingerprint: string | null;
  issues: SaveRefusalIssues;
}): StoredSaveRefusal | null {
  if (refusal.fingerprint === null) return null;
  return {
    fingerprint: refusal.fingerprint,
    byQuestionId: [...refusal.issues.byQuestionId],
    byAssetId: [...refusal.issues.byAssetId],
    unscoped: refusal.issues.unscoped,
  };
}

function refusalFromStorage(stored: StoredSaveRefusal): {
  fingerprint: string;
  issues: SaveRefusalIssues;
} {
  return {
    fingerprint: stored.fingerprint,
    issues: {
      byQuestionId: new Map(stored.byQuestionId),
      byAssetId: new Map(stored.byAssetId),
      unscoped: stored.unscoped,
    },
  };
}

/** The tested item an operation of this batch writes, or null. */
function refusedAsset(
  local: QuestionnaireDraft,
  operations: Array<QuestionnaireHttpOperation>,
  operationIndex: number | null | undefined,
): string | null {
  const operation =
    typeof operationIndex === "number" ? operations[operationIndex] : undefined;
  const stableKey =
    operation?.op === "add_asset"
      ? operation.asset.stableKey
      : operation?.op === "edit_asset" || operation?.op === "set_asset_media"
        ? operation.target
        : undefined;
  if (!stableKey) return null;
  const asset = (local.testedAssets?.assets ?? []).find(
    (candidate) => candidate.stableKey === stableKey,
  );
  return asset?.id ?? null;
}

/** A draft's JSON with its write timestamps blanked: stamps are not edits. */
function withoutWriteTimestamps(json: string): string {
  return json.replace(/"updatedAt":"[^"]*"/g, '"updatedAt":"<ts>"');
}

// A newer accepted draft arrived under unsaved edits: they stay as they are,
// and the next Save applies them to that draft (L12).
function warnNewerDraft(): void {
  toast.warning(i18n.t("questionnaire.rebaseConflict", { ns: "app" }), {
    position: SAVE_TOAST_POSITION,
  });
}

/**
 * The one line for a Save the draft moved under (409), said once the draft it
 * handed back is in (E-session-05): the edits are kept, and the next Save
 * applies them to that draft.
 */
function announceSaveConflict(conflictCopy: {
  title: string;
  description: string | null;
}): void {
  toast.error(conflictCopy.title, {
    description: conflictCopy.description ?? undefined,
    position: SAVE_TOAST_POSITION,
  });
}

/**
 * One objective write, through the objective's own route (R4). `learn`
 * omitted keeps the bullets the draft already carries (the coverage pass
 * reads them, so a wording tweak must not drop them); `source` omitted means
 * the marketer typed it, so it lands as 'edited'. Legacy callers may still
 * pass 'confirmed'; the row no longer does.
 */
export interface StudyObjectiveUpdate {
  decision: string;
  learn?: Array<string>;
  source?: StudyGoals["source"];
}

interface UseQuestionnaireWorkspaceOptions {
  assumptions: Array<string>;
  /**
   * Save the edits from `baseDraft` (the accepted draft they started from) to
   * `draft` at `expectedRevision`: one request, typed operations.
   */
  onSaveDraft: (
    baseDraft: QuestionnaireDraft,
    draft: QuestionnaireDraft,
    expectedRevision?: number,
  ) => Promise<SaveDraftResult | void>;
  /**
   * The server's dry run of the Save `onSaveDraft` would send (the same batch,
   * nothing written): the one validity verdict (T1b). Null when there is
   * nothing to send.
   */
  onValidateDraft?: (
    baseDraft: QuestionnaireDraft,
    draft: QuestionnaireDraft,
    expectedRevision?: number,
  ) => Promise<QuestionnaireValidateResponse | null>;
  selectedPopulation: SelectedPopulation | null;
  serverDraft: QuestionnaireDraft | null;
  serverRevision?: number;
  serverSurveyJson: SurveyJson | null;
}

export interface QuestionnaireWorkspaceState {
  currentDraft: QuestionnaireDraft | null;
  currentSurveyJson: SurveyJson | null;
  discardChanges: () => void;
  duplicateQuestion: (questionId: string) => void;
  estimatedMinutes: number;
  conceptAssets: Array<ConceptAsset>;
  /** The saved and pending catalogs' mention tables, each built once per catalog. */
  mentionTables: AssetMentionTables;
  isDirty: boolean;
  isSaving: boolean;
  questionCount: number;
  questions: Array<QuestionnaireQuestionRecord>;
  saveChanges: () => Promise<WorkspaceSaveOutcome>;
  selectedPopulation: SelectedPopulation | null;
  selectedQuestionId: string | null;
  setSelectedQuestionId: (questionId: string | null) => void;
  surveyDescription: string;
  /** The decision this study informs. Empty only for pre-objective drafts. */
  studyObjective: string;
  /**
   * Where the objective came from. 'inferred' is ours and is shown as a
   * suggestion; the other two are the marketer's own words. Null when the draft
   * predates the field.
   */
  studyObjectiveSource: StudyGoals["source"] | null;
  /**
   * What the last refused Save names, by question id, while the draft on
   * screen is the one it refused: an edit, Undo or Redo away from it hides
   * it, and coming back shows it again. Cleared by the next Save or
   * Discard. Optional so hand-built test stubs can omit it.
   */
  saveRefusal?: SaveRefusalIssues;
  /**
   * The server's dry run of the unsaved Save (`onValidateDraft`), by question
   * id: what the Save's result would still have and what it would change
   * (`dryRunIssuesByQuestionId`). Empty while nothing is unsaved or the dry
   * run is behind the draft on screen. Optional so hand-built test stubs can
   * omit it.
   */
  logicIssues?: DryRunIssues;
  /**
   * Why the Save is held back: the dry run of the draft on screen would be
   * refused, in the words the refused Save's toast uses (owner, 2026-10-22).
   * Null when the Save may go. Optional so hand-built test stubs can omit it.
   */
  saveHeldBack?: { title: string; description: string | null } | null;
  /**
   * Persisted draft parsed from the last saved snapshot. Readiness issues
   * must be indexed against this, never the live editable copy.
   * Optional so test fixtures can omit it; the panel falls back to `draft`.
   */
  savedDraft?: QuestionnaireDraft | null;
  title: string;
  updateQuestion: (
    questionId: string,
    updater: (question: CanonicalEditableQuestion) => CanonicalEditableQuestion,
  ) => void;
  updateQuestionOrderSelection: (questionId: string | null) => void;
  updateSurveyMeta: (patch: { title?: string; description?: string }) => void;
  addConceptAsset: (type?: TestedAssetType) => void;
  assetUploadsInFlight: boolean;
  attachConceptImage: (conceptId: string, file: File) => Promise<void>;
  /** A page capture the backend stored: the asset's image, as an unsaved edit. */
  attachConceptCapture: (conceptId: string, file: IssuedAssetFile, url: string) => void;
  attachConceptUrl: (conceptId: string, url: string) => Promise<void>;
  removeConceptAsset: (conceptId: string) => void;
  /**
   * Replace the block's declared assetTypes (the panel's header type-switch).
   * Replacement, not union: the derive path only ever adds, so a pass through
   * a text-collect type would otherwise stay declared forever and wedge the
   * send guard.
   */
  setDeclaredAssetTypes: (types: Array<TestedAssetType>) => void;
  updateConceptAsset: (conceptId: string, patch: Partial<ConceptAsset>) => void;
  /**
   * Concepts whose new video the Save in flight is watching (card spinner).
   * Optional on the interface so a hand-built workspace stub in a test does not
   * have to know about it.
   */
  analyzingConceptIds?: Set<string>;
  addQuestion: () => void;
  deleteQuestion: (questionId: string) => void;
  changeQuestionType: (questionId: string, nextType: string) => void;
  reorderResearchQuestions: (fromId: string, toId: string) => void;
  /**
   * Move one of the two declared exposure marks (Phase 4b-d). `stableKey` is
   * the last pre-exposure question for `"reveal"`, or the first shared-view
   * question for `"sharedView"`; `null` declares an empty zone (nothing is
   * pre-exposure, or lanes run to the end). Always stamps `source: "user"` —
   * the agent only ever writes a mark at Build or RePlan. Saved as a
   * `set_exposure_boundary` operation on the next Save.
   */
  setExposureMark: (
    mark: QuestionnaireExposureMarkName,
    stableKey: string | null,
  ) => void;
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  /** Monotonic revision bumped on every local draft mutation (for race gates). */
  getLocalDraftRevision: () => number;
}

export const QUESTIONNAIRE_RESEARCH_TYPE_OPTIONS: Array<QuestionnaireTypeOption> =
  [
    {
      id: "single_select",
      label: "Single choice",
      badgeLabel: "Single choice",
      tone: "green",
    },
    {
      id: "multi_select",
      label: "Multi-select",
      badgeLabel: "Multi-select",
      tone: "green",
    },
    { id: "rating", label: "Rating", badgeLabel: "Rating", tone: "coral" },
    { id: "ranking", label: "Ranking", badgeLabel: "Ranking", tone: "coral" },
    { id: "continuous", label: "Number", badgeLabel: "Number", tone: "coral" },
    {
      id: "matrix_rating",
      label: "Rating matrix",
      badgeLabel: "Rating matrix",
      tone: "green",
    },
    {
      id: "matrix_categorical",
      label: "Choice matrix",
      badgeLabel: "Choice matrix",
      tone: "green",
    },
    { id: "open_end", label: "Open end", badgeLabel: "Open end", tone: "gray" },
  ];

function cloneDraft(
  draft: QuestionnaireDraft | null,
): QuestionnaireDraft | null {
  return draft
    ? (JSON.parse(JSON.stringify(draft)) as QuestionnaireDraft)
    : null;
}

function cloneSurveyJson(surveyJson: SurveyJson | null): SurveyJson | null {
  return surveyJson
    ? (JSON.parse(JSON.stringify(surveyJson)) as SurveyJson)
    : null;
}

// Every key the builder mints (question stable keys, answer values, measure
// ids) follows the one key rule (`config/questionnaire-key-rules.json`, the
// shared-types `identity` module), so the write normaliser keeps it as it is.

// An item's identity (its `id`, and its `value` where it has one) is kept
// while it is still free in its list: an edit to the label never changes it,
// because logic, analysis and the compiled survey join on it. An item without
// one (a new row, option, category or measure) mints it once, from its label,
// unique in its list. A duplicate left by an older draft is re-minted, which
// heals it the first time the list is edited.
function keepOrMint(
  preferred: string | undefined,
  label: string,
  fallback: string,
  taken: Set<string>,
): string {
  if (preferred && !taken.has(preferred.toLowerCase())) {
    taken.add(preferred.toLowerCase());
    return preferred;
  }
  return mintAnswerValue(label, fallback, taken);
}

/** A list item as an editor hands it back; a new item has no id/value yet. */
type ItemEdit<T extends { id: string }> = Omit<T, "id" | "value"> &
  Partial<Pick<T, Extract<keyof T, "id" | "value">>>;

type ValueItem = {
  id: string;
  value: string;
  label: string;
  assetRefs?: Array<unknown>;
};

/** A question's value-bearing lists: its options, or a grid's fixed rows and categories. */
function valueItemLists(response: unknown): {
  choices?: Array<ValueItem>;
  rows?: Array<ValueItem>;
} {
  const r = (response ?? {}) as {
    kind?: string;
    options?: Array<ValueItem>;
    categories?: Array<ValueItem>;
    rows?: { source?: string; rows?: Array<ValueItem> };
  };
  const rows =
    r.rows?.source === "fixed" && Array.isArray(r.rows.rows)
      ? r.rows.rows
      : undefined;
  if (Array.isArray(r.options)) return { choices: r.options };
  if (r.kind === "matrix_categorical")
    return { choices: r.categories, rows };
  if (r.kind === "matrix_rating") return { rows };
  return {};
}

/**
 * A list with its new items' values taken from their labels, and the renames
 * it made (old value to new). An item is new when the saved list has no item
 * with its id. A kept item, an asset's answer (its key is the server's) and a
 * label with nothing to slug (blank, the Save refuses it) keep their value.
 */
function listWithLabelValues(
  response: unknown,
  items: Array<ValueItem>,
  savedIds: ReadonlySet<string>,
): { items: Array<ValueItem>; renames: Map<string, string> } {
  const fixed = (item: ValueItem) =>
    savedIds.has(item.id) ||
    (Array.isArray(item.assetRefs) && item.assetRefs.length > 0) ||
    isRosterAnswer(response, item) ||
    !slugKey(item.label ?? "");
  const taken = takenKeys(items.filter(fixed).map((item) => item.value));
  const renames = new Map<string, string>();
  const next = items.map((item) => {
    if (fixed(item)) return item;
    const value = mintAnswerValue(item.label, "", taken);
    if (value === item.value) return item;
    renames.set(item.value, value);
    return { ...item, value };
  });
  return { items: renames.size > 0 ? next : items, renames };
}

/**
 * The first Save fixes a new item's value from its label (S1.5). An item's
 * value is its answer key: logic, analysis, the compiled survey and the data
 * export join on it, and from its first Save its id keeps it (a changed value
 * on a kept id is refused as `item_identity_changed`). Until that Save, the
 * value the editor minted when the item was added came from its placeholder
 * ("Option 7" gave `option_7`), so this gives a new option, fixed row or
 * category (one the saved draft's same question does not have, or any item of
 * a new question) the slug of the label the author typed, unique in its list.
 * Conditions that name a renamed value (visibility and routing, on this
 * question) are re-pointed with it: that value was never saved, so only this
 * draft knows it. A carry-forward's options are derived from its source again
 * (`withCarriedChoices`, the server's own derive). Returns `local` itself
 * when nothing changes. A slug is an identifier, not a reading of the label.
 */
export function withSavedItemValues(
  saved: QuestionnaireDraft,
  local: QuestionnaireDraft,
): QuestionnaireDraft {
  const savedByKey = new Map(
    saved.researchQuestions.map((question) => [question.stableKey, question]),
  );
  const renamesByKey = new Map<
    string,
    { choices: Map<string, string>; rows: Map<string, string> }
  >();
  const minted = local.researchQuestions.map((question) => {
    const response = question.response as unknown as Record<
      string,
      unknown
    > & { choicesFrom?: ChoicesFromRef };
    // A carry-forward's options are its source's, derived below.
    if (response.choicesFrom) return question;
    const lists = valueItemLists(response);
    const savedLists = valueItemLists(
      savedByKey.get(question.stableKey)?.response,
    );
    const idsOf = (items?: Array<ValueItem>) =>
      new Set((items ?? []).map((item) => item.id));
    const choices = lists.choices
      ? listWithLabelValues(response, lists.choices, idsOf(savedLists.choices))
      : null;
    const rows = lists.rows
      ? listWithLabelValues(response, lists.rows, idsOf(savedLists.rows))
      : null;
    if (!choices?.renames.size && !rows?.renames.size) return question;
    renamesByKey.set(question.stableKey, {
      choices: choices?.renames ?? new Map(),
      rows: rows?.renames ?? new Map(),
    });
    const next: Record<string, unknown> = { ...response };
    if (choices?.renames.size) {
      next[Array.isArray(response.options) ? "options" : "categories"] =
        choices.items;
    }
    if (rows?.renames.size) {
      next.rows = { ...(response.rows as object), rows: rows.items };
    }
    return { ...question, response: next as unknown as ResponseDefinition };
  });
  if (renamesByKey.size === 0) return local;

  const renamedLeaf = <T extends Record<string, unknown>>(leaf: T): T => {
    const renames = renamesByKey.get(leaf.dependsOnStableKey as string);
    if (!renames) return leaf;
    const values = leaf.values as Array<string> | undefined;
    const row = leaf.matrixRowValue as string | undefined;
    return {
      ...leaf,
      ...(Array.isArray(values)
        ? { values: values.map((value) => renames.choices.get(value) ?? value) }
        : {}),
      ...(typeof row === "string"
        ? { matrixRowValue: renames.rows.get(row) ?? row }
        : {}),
    };
  };
  const renamedExpr = <T,>(expr: T): T => {
    if (!expr || typeof expr !== "object") return expr;
    const record = expr as Record<string, unknown>;
    if (Array.isArray(record.rules)) {
      return {
        ...record,
        rules: (record.rules as Array<Record<string, unknown>>).map(renamedLeaf),
      } as T;
    }
    return renamedLeaf(record) as T;
  };

  return {
    ...local,
    researchQuestions: withCarriedChoices(minted.map((question) => ({
        ...question,
        ...(question.visibilityRule
          ? { visibilityRule: renamedExpr(question.visibilityRule) }
          : {}),
        ...(question.routingRule
          ? {
              routingRule: {
                ...question.routingRule,
                when: renamedExpr(question.routingRule.when),
              },
            }
          : {}),
    }))),
  };
}

function choiceOptions(
  prefix: string,
  labels: Array<string>,
): Array<ChoiceOption> {
  const takenValues = new Set<string>();
  return labels.map((label, index) => ({
    id: `${prefix}_${index + 1}`,
    value: mintAnswerValue(label, `${prefix}_${index + 1}`, takenValues),
    label,
  }));
}

/** A new list's two items: blank, shown with their placeholders (FE11). */
const BLANK_PAIR: Array<string> = ["", ""];

function matrixRows(prefix: string, labels: Array<string>): Array<MatrixRow> {
  const takenValues = new Set<string>();
  return labels.map((label, index) => ({
    id: `${prefix}_${index + 1}`,
    value: mintAnswerValue(label, `${prefix}_${index + 1}`, takenValues),
    label,
  }));
}

export function extractChoiceOptions<Q extends RespondentQuestion>(
  question: Q,
): Array<ChoiceOption> {
  const response = question.response as ResponseDefinition;
  if ("options" in response && Array.isArray(response.options)) {
    return response.options;
  }
  return [];
}

export function extractRows<Q extends RespondentQuestion>(
  question: Q,
  concepts: Array<ConceptAsset> = [],
): Array<string> {
  const response = question.response as any;
  if (response?.rows?.source === "tested_assets") {
    // Mention form, because these labels are what the editor hands back when an
    // author edits one of them — a plain name would untag every other row.
    // Rows follow the bound `assetType` the compiler will filter on, so the
    // builder shows the grid that will actually ship. A draft whose declared
    // type matches nothing (a mis-stamped older draft) keeps every asset rather
    // than rendering an empty grid. The list is the one `withEditableAssetRows`
    // converts on the first edit, so a displayed row index names that row.
    const boundType = response.rows.assetType;
    const bound = concepts.filter((concept) => concept.type === boundType);
    const rowConcepts = bound.length > 0 ? bound : concepts;
    return rowConcepts.map((concept) => `@${concept.name}`);
  }
  if (response?.rows?.source === "fixed" && Array.isArray(response.rows.rows)) {
    // Keep blanked labels (do NOT filter) so a row a user is mid-rename stays
    // VISIBLE + editable in the builder and never becomes an invisible orphan
    // that compiles into a phantom respondent row. Empty rows are dropped at
    // compile and flagged by the validator.
    return response.rows.rows.map((row: MatrixRow) => row.label ?? "");
  }
  return [];
}

export function extractColumns<Q extends RespondentQuestion>(
  question: Q,
): Array<string> {
  const response = question.response as any;
  if (response?.kind === "matrix_rating" && Array.isArray(response.measures)) {
    // Keep blanked labels visible/editable (see extractRows) — no orphan columns.
    return response.measures.map(
      (measure: MatrixRatingMeasure) => measure.label ?? "",
    );
  }
  if (
    response?.kind === "matrix_categorical" &&
    Array.isArray(response.categories)
  ) {
    return response.categories.map(
      (option: ChoiceOption) => option.label ?? "",
    );
  }
  return [];
}

export interface VisibilityRuleMatrixRowOption {
  value: string;
  label: string;
}

export interface VisibilityRuleMatrixMeasureOption {
  id: string;
  label: string;
  unit: RatingUnitId;
  rateMin: number;
  rateMax: number;
  includeNotApplicable?: boolean;
  notApplicableLabel?: string;
}

export interface VisibilityRuleCandidate {
  stableKey: string;
  title: string;
  kind:
    | "choice"
    | "rating"
    | "continuous"
    | "matrix_rating"
    | "matrix_categorical"
    | "ranking";
  options: Array<ChoiceOption>;
  /** True when kind is choice and the dependency is multi_select. */
  isMultiSelect?: boolean;
  unit?: RatingUnitId | ContinuousUnitId;
  rateMin?: number;
  rateMax?: number;
  min?: number;
  max?: number;
  maxRank?: number;
  includeNotApplicable?: boolean;
  notApplicableLabel?: string;
  matrixRows?: Array<VisibilityRuleMatrixRowOption>;
  matrixMeasures?: Array<VisibilityRuleMatrixMeasureOption>;
  /** A stored rule still names this source, but its declared exposure phase cannot use it. */
  unavailableAtExposure?: boolean;
}

function extractMatrixVisibilityRows(
  question: CanonicalEditableQuestion,
  testedAssets: Array<{ id: string; type?: string; name?: string }>,
): Array<VisibilityRuleMatrixRowOption> {
  const response = question.response as {
    rows?: {
      source?: string;
      rows?: Array<MatrixRow>;
      assetType?: string;
    };
  };
  const rows = response.rows;
  if (!rows) {
    return [];
  }
  if (rows.source === "fixed") {
    return (rows.rows ?? [])
      .filter((row) => typeof row.value === "string" && row.value.length > 0)
      .map((row) => ({
        value: row.value,
        label: row.label?.trim() || row.value,
      }));
  }
  if (rows.source === "tested_assets" && typeof rows.assetType === "string") {
    return testedAssets
      .filter((asset) => asset.type === rows.assetType && asset.id)
      .map((asset) => ({
        value: asset.id,
        label: asset.name?.trim() || asset.id,
      }));
  }
  return [];
}

function extractMatrixVisibilityMeasures(
  question: CanonicalEditableQuestion,
): Array<VisibilityRuleMatrixMeasureOption> {
  const response = question.response as {
    measures?: Array<MatrixRatingMeasure>;
  };
  return (response.measures ?? [])
    .filter(
      (measure) =>
      typeof measure.id === "string" &&
        measure.id.length > 0 &&
        typeof measure.unit === "string",
    )
    .map((measure) => {
      const [rateMin, rateMax] = ratingBounds(measure.unit);
      return {
        id: measure.id,
        label: measure.label?.trim() || measure.id,
        unit: measure.unit,
        rateMin,
        rateMax,
        ...(measure.includeNotApplicable === true
          ? {
              includeNotApplicable: true,
              ...(measure.notApplicableLabel
                ? { notApplicableLabel: measure.notApplicableLabel }
                : {}),
            }
          : {}),
      };
    });
}

function visibilityCandidateForQuestion(
  question: CanonicalEditableQuestion,
  testedAssets: Array<{ id: string; type?: string; name?: string }>,
): Array<VisibilityRuleCandidate> {
  if (SELECT_QUESTION_TYPES.has(question.type)) {
    return [
      {
        stableKey: question.stableKey,
        title: question.title?.trim() || "Untitled question",
        kind: "choice",
        isMultiSelect: question.type === "multi_select",
        options: extractChoiceOptions(question),
      },
    ];
  }
  if (question.type === "rating") {
    const response = question.response as {
      unit?: RatingUnitId;
      includeNotApplicable?: boolean;
      notApplicableLabel?: string;
    };
    if (!response.unit) {
      return [];
    }
    const [rateMin, rateMax] = ratingBounds(response.unit);
    return [
      {
        stableKey: question.stableKey,
        title: question.title?.trim() || "Untitled question",
        kind: "rating",
        options: [],
        unit: response.unit,
        rateMin,
        rateMax,
        ...(response.includeNotApplicable === true
          ? {
              includeNotApplicable: true,
              ...(response.notApplicableLabel
                ? { notApplicableLabel: response.notApplicableLabel }
                : {}),
            }
          : {}),
      },
    ];
  }
  if (question.type === "matrix_rating") {
    const matrixRows = extractMatrixVisibilityRows(question, testedAssets);
    const matrixMeasures = extractMatrixVisibilityMeasures(question);
    if (matrixRows.length === 0 || matrixMeasures.length === 0) {
      return [];
    }
    return [
      {
        stableKey: question.stableKey,
        title: question.title?.trim() || "Untitled question",
        kind: "matrix_rating",
        options: [],
        matrixRows,
        matrixMeasures,
        unit: matrixMeasures[0]?.unit,
        rateMin: matrixMeasures[0]?.rateMin,
        rateMax: matrixMeasures[0]?.rateMax,
      },
    ];
  }
  if (question.type === "matrix_categorical") {
    const matrixRows = extractMatrixVisibilityRows(question, testedAssets);
    const response = question.response as { categories?: Array<ChoiceOption> };
    const options = response.categories ?? [];
    if (matrixRows.length === 0 || options.length === 0) {
      return [];
    }
    return [
      {
        stableKey: question.stableKey,
        title: question.title?.trim() || "Untitled question",
        kind: "matrix_categorical",
        options,
        matrixRows,
      },
    ];
  }
  if (question.type === "ranking") {
    const options = extractChoiceOptions(question);
    const maxRank = rankingMaxLength(
      question.response as unknown as Record<string, unknown>,
    );
    if (options.length === 0 || maxRank < 1) {
      return [];
    }
    return [
      {
        stableKey: question.stableKey,
        title: question.title?.trim() || "Untitled question",
        kind: "ranking",
        options,
        maxRank,
      },
    ];
  }
  if (question.type !== "continuous") {
    return [];
  }
  const response = question.response as {
    unit?: ContinuousUnitId;
    min?: number;
    max?: number;
  };
  if (!response.unit) {
    return [];
  }
  const bounds = continuousThresholdBounds(response);
  return [
    {
      stableKey: question.stableKey,
      title: question.title?.trim() || "Untitled question",
      kind: "continuous",
      options: [],
      unit: response.unit,
      ...(typeof response.min === "number" ? { min: response.min } : {}),
      ...(typeof response.max === "number" ? { max: response.max } : {}),
      ...(bounds && Number.isFinite(bounds[0]) ? { rateMin: bounds[0] } : {}),
      ...(bounds && Number.isFinite(bounds[1]) ? { rateMax: bounds[1] } : {}),
    },
  ];
}

export interface ChoicesFromCandidate {
  stableKey: string;
  title: string;
  options: Array<ChoiceOption>;
  /** The source question itself, which the contract's carry-forward rules read. */
  source: CanonicalEditableQuestion;
}

/** Order in which a new or re-pointed carry-forward takes its first accepted mode. */
const CHOICES_FROM_MODE_PREFERENCE: Array<ChoicesFromRef["mode"]> = [
  "selected",
  "all",
  "unselected",
];

/**
 * The modes the contract accepts for carrying `source`'s options into
 * `target` (the negation of `choices_from_cardinality_unreachable`), in
 * preference order. Editors offer and default to these only.
 */
export function reachableChoicesFromModes(
  source: RespondentQuestion,
  target: RespondentQuestion,
): Array<ChoicesFromRef["mode"]> {
  return CHOICES_FROM_MODE_PREFERENCE.filter((mode) =>
    isChoicesFromModeReachable(source, target, mode),
  );
}

/**
 * Earlier single_select/multi_select questions a choicesFrom may reference:
 * those with at least two options and a mode the contract accepts for this
 * target. A ranked list carries no exclusive escape (D0a) while a carried
 * list must mirror the source's exclusive flags, so a source with one can't
 * feed a ranking. A question that compares tested assets answers with them, so
 * only a list of assets can feed it.
 */
export function getChoicesFromCandidates(
  researchQuestions: Array<CanonicalEditableQuestion>,
  questionId: string,
): Array<ChoicesFromCandidate> {
  const index = researchQuestions.findIndex(
    (question) => question.id === questionId,
  );
  if (index <= 0) {
    return [];
  }
  const target = researchQuestions[index];
  return researchQuestions
    .slice(0, index)
    .filter((question) => CHOICES_FROM_SOURCE_QUESTION_TYPES.has(question.type))
    .map((question) => ({
      stableKey: question.stableKey,
      title: question.title?.trim() || "Untitled question",
      options: extractChoiceOptions(question),
      source: question,
    }))
    .filter(
      (candidate) =>
        candidate.options.length >= 2 &&
        !(
          target.type === "ranking" &&
          candidate.options.some((option) => option.exclusive === true)
        ) &&
        reachableChoicesFromModes(candidate.source, target).length > 0 &&
        // A question that compares assets takes only a list of assets.
        carriedListKeepsComparison(target, candidate.options, "all"),
    );
}

// Earlier single_select/multi_select/rating/continuous/matrix_rating/
// matrix_categorical/ranking
// questions a given question's `visibilityRule` may depend on, in the same
// order/shape the backend validator enforces (see canonical-questionnaire.md):
// strictly earlier in `researchQuestions`. The CURRENT question's type is
// unrestricted — open_end and matrix_categorical may be skipped/shown based on
// an earlier eligible dependency. Rating/matrix measure candidates omit
// includeNotApplicable scales.
export function getVisibilityRuleCandidates(
  researchQuestions: Array<CanonicalEditableQuestion>,
  questionId: string,
  testedAssets: Array<{ id: string; type?: string; name?: string }> = [],
  exposureDraft?: unknown,
  preserveStableKeys: ReadonlySet<string> = new Set(),
): Array<VisibilityRuleCandidate> {
  const index = researchQuestions.findIndex(
    (question) => question.id === questionId,
  );
  if (index < 0) {
    return [];
  }
  const target = researchQuestions[index];
  return researchQuestions
    .flatMap((question, sourceIndex) => {
      const available =
        sourceIndex < index &&
        isQuestionDependencyAvailableAtExposure(
          exposureDraft,
          target.stableKey,
          question.stableKey,
        );
      if (!available && !preserveStableKeys.has(question.stableKey)) {
        return [];
      }
      return visibilityCandidateForQuestion(question, testedAssets).map(
        (candidate) =>
          available
            ? candidate
            : { ...candidate, unavailableAtExposure: true },
      );
    });
}

export interface RoutingTargetCandidate {
  stableKey: string;
  title: string;
  /**
   * False when this question is asked in another pass than the host
   * (a repeating lane versus the shared view). Omitted when the survey
   * is one pass, or the target shares the host's pass.
   */
  samePass?: boolean;
}

/**
 * Candidates for routingRule.when: earlier eligible questions, then the host.
 * Empty when the host itself cannot be a condition dependency (routing requires
 * a primary leaf on this question). Additional compound leaves may pick any
 * candidate in this list (host or earlier).
 */
export function getRoutingWhenCandidates(
  researchQuestions: Array<CanonicalEditableQuestion>,
  questionId: string,
  testedAssets: Array<{ id: string; type?: string; name?: string }> = [],
  exposureDraft?: unknown,
  preserveStableKeys: ReadonlySet<string> = new Set(),
): Array<VisibilityRuleCandidate> {
  const index = researchQuestions.findIndex(
    (question) => question.id === questionId,
  );
  if (index < 0) {
    return [];
  }
  const host = researchQuestions[index];
  const hostCandidates = visibilityCandidateForQuestion(host, testedAssets);
  if (hostCandidates.length === 0) {
    return [];
  }
  const earlier = researchQuestions.flatMap((question, sourceIndex) => {
    if (sourceIndex === index) return [];
    const available =
      sourceIndex < index &&
      isQuestionDependencyAvailableAtExposure(
        exposureDraft,
        host.stableKey,
        question.stableKey,
      );
    if (!available && !preserveStableKeys.has(question.stableKey)) return [];
    return visibilityCandidateForQuestion(question, testedAssets).map(
      (candidate) =>
        available
          ? candidate
          : { ...candidate, unavailableAtExposure: true },
    );
  });
  return [...earlier, ...hostCandidates];
}

/**
 * Later research questions a routingRule skip_to may land on, in order.
 * The first entry is the immediate next question. A skip lands on its
 * target, so that entry skips nothing and is not a valid default.
 *
 * When `exposureDraft` has repeating passes, a candidate in another pass
 * is marked `samePass: false`. Those targets fail `route_target_other_pass`
 * and are not valid defaults.
 */
export function getRoutingTargetCandidates(
  researchQuestions: Array<CanonicalEditableQuestion>,
  questionId: string,
  exposureDraft?: unknown,
): Array<RoutingTargetCandidate> {
  const index = researchQuestions.findIndex(
    (question) => question.id === questionId,
  );
  if (index < 0 || index >= researchQuestions.length - 1) {
    return [];
  }
  const exposure =
    exposureDraft == null ? null : exposureZonesOf(exposureDraft);
  const zones = exposure?.separatePasses ? exposure.zones : null;
  const hostZone = zones?.get(researchQuestions[index].stableKey);
  return researchQuestions.slice(index + 1).map((question) => {
    const samePass =
      zones == null || zones.get(question.stableKey) === hostZone;
    return {
      stableKey: question.stableKey,
      title: question.title?.trim() || "Untitled question",
      ...(samePass ? {} : { samePass: false }),
    };
  });
}

/**
 * The question a new skip lands on.
 *
 * Same-pass candidates are later questions in order, so the first is the
 * immediate next page and landing there skips nothing. Prefer the second
 * later same-pass question. Undefined when fewer than two same-pass
 * questions follow, which is when a new skip ends the survey instead.
 */
export function defaultSkipTargetStableKey(
  targetCandidates: ReadonlyArray<
    Pick<RoutingTargetCandidate, "stableKey" | "samePass">
  >,
): string | undefined {
  const samePassTargets = targetCandidates.filter(
    (candidate) => candidate.samePass !== false,
  );
  return samePassTargets[1]?.stableKey;
}

/** True when the target is the immediate next question in `targetCandidates`. */
export function isImmediateNextSkipTarget(
  targetCandidates: ReadonlyArray<Pick<RoutingTargetCandidate, "stableKey">>,
  targetStableKey: string | null | undefined,
): boolean {
  return (
    Boolean(targetStableKey) &&
    targetCandidates[0]?.stableKey === targetStableKey
  );
}

function withVisibilityRule(
  question: CanonicalEditableQuestion,
  rule: QuestionVisibilityRuleExpr | undefined,
): CanonicalEditableQuestion {
  if (!rule) {
    const { visibilityRule: _drop, ...rest } = question;
    return rest;
  }
  return { ...question, visibilityRule: rule };
}

function withChoicesFrom<Q extends RespondentQuestion>(
  question: Q,
  choicesFrom: ChoicesFromRef | undefined,
): Q {
  const response = question.response as ResponseDefinition & {
    choicesFrom?: ChoicesFromRef;
  };
  if (!choicesFrom) {
    if (!("choicesFrom" in response) || response.choicesFrom === undefined) {
      return question;
    }
    const { choicesFrom: _drop, ...rest } = response;
    return { ...question, response: rest as ResponseDefinition };
  }
  return {
    ...question,
    response: { ...response, choicesFrom } as ResponseDefinition,
  };
}

function withRoutingRule(
  question: CanonicalEditableQuestion,
  rule: QuestionRoutingRule | undefined,
): CanonicalEditableQuestion {
  if (!rule) {
    const { routingRule: _drop, ...rest } = question;
    return rest;
  }
  return { ...question, routingRule: rule };
}

// A grid measure's anchors come from the draft language's default rating
// template, as a rating's labels do: the one seed for a measure the type
// change creates and a measure the editor adds.
function defaultMeasureAnchors(language: string): MatrixRatingMeasure["anchors"] {
  const labels = ratingScaleTemplatesFor(language)[DEFAULT_RATING_TEMPLATE_KEY]
    .labels;
  return { minLabel: labels[0], maxLabel: labels[labels.length - 1] };
}

export function defaultResponseForType(
  type: QuestionDefinition["type"],
  stableKey: string,
  language = "en",
): ResponseDefinition {
  // Lists start blank, shown with their placeholders, as a new question does
  // (`buildDefaultResearchQuestion`): seeded words in one language reached
  // twins, typing appended to them, and a seeded "Other" was neither
  // keep-last nor fixed under a shuffle (QA4 FE11).
  if (type === "multi_select") {
    return {
      kind: "multi_select",
      options: choiceOptions(stableKey, BLANK_PAIR),
      minSelected: 1,
      // No maxSelected: absent is "select any", which the editor has no
      // control for; a seeded 3 went stale as options were added (LP N9).
      // Matches B1: an assembled multi_select is randomized by default
      // (RND-2548, position-bias counter). A newly created one states the
      // same intent from the start.
      randomizeOptions: true,
    };
  }
  // New ratings are born on a real wording template, not a placeholder (a
  // scale is always a dictionary template or Custom, never "empty"), in the
  // draft's language; a rating grid's anchors come from the same template
  // (`defaultMeasureAnchors`).
  const templateLabels = Array.from<string>(
    ratingScaleTemplatesFor(language)[DEFAULT_RATING_TEMPLATE_KEY].labels,
  );
  if (type === "rating") {
    const unit = "rating_1_5";
    return {
      kind: "rating",
      unit,
      pointLabels: templateLabels,
      anchors: {
        minLabel: templateLabels[0],
        midpointLabel: ratingMidpointLabel(unit, templateLabels[2]),
        maxLabel: templateLabels[4],
      },
      includeNotApplicable: false,
    };
  }
  if (type === "ranking") {
    return {
      kind: "ranking",
      options: choiceOptions(stableKey, BLANK_PAIR),
    };
  }
  if (type === "continuous") {
    return {
      kind: "continuous",
      unit: "percent_0_100",
      min: 0,
      max: 100,
      precision: 1,
    };
  }
  if (type === "open_end") {
    return {
      kind: "open_end",
      maxLength: 600,
      followUpToQuestionIds: [],
    };
  }
  if (type === "matrix_rating") {
    return {
      kind: "matrix_rating",
      rows: {
        source: "fixed",
        rows: matrixRows(`${stableKey}_row`, BLANK_PAIR),
      },
      // Blank, like the rows: the author names what each column measures.
      measures: BLANK_PAIR.map((label, index) => ({
        id: `${stableKey}_measure_${index + 1}`,
        label,
        unit: "rating_1_5",
        anchors: defaultMeasureAnchors(language),
        includeNotApplicable: false,
      })),
      // Grid rows are randomized by default, stated explicitly like
      // `randomizeOptions`.
      randomizeRows: true,
    };
  }
  if (type === "matrix_categorical") {
    return {
      kind: "matrix_categorical",
      rows: {
        source: "fixed",
        rows: matrixRows(`${stableKey}_row`, BLANK_PAIR),
      },
      categories: choiceOptions(`${stableKey}_cat`, BLANK_PAIR),
      allowNone: false,
      randomizeRows: true,
    };
  }
  return {
    kind: "single_select",
    options: choiceOptions(stableKey, BLANK_PAIR),
    randomizeOptions: false,
  };
}

/** The select's options with its pinned answers last, unless the list is the user's own order (shared-types `selectOptionsOrder`). */
function sortPinnedLast(options: Array<ChoiceOption>, givenOrder?: unknown): Array<ChoiceOption> {
  return selectOptionsOrder(options, givenOrder) as Array<ChoiceOption>;
}

// Keep a response internally coherent regardless of how it was produced (manual
// edit, type switch, AI). Pure + idempotent. Currently: hold a multi_select's
// selection counts within its option count (shared-types
// `selectionCountsHeld`, as the asset-removal cleanup does) and hold a
// ranking's stated counts inside their one range (shared-types
// `rankingCountsHeld`, the range the write gate checks), keep single_select/
// multi_select pinned answers last, catch-alls then `exclusive` opt-outs,
// regardless of where they were placed or marked (QA2 A3, E-questions-09:
// shared-types `pinnedLastOrder`, the order the operations applier keeps), and strip the `exclusive` opt-out flag
// off ranking (D0a excludes it explicitly: an exclusive item inside a ranked
// list is incoherent), with `keepLast` (every ranked item is ranked). Per
// D0/D0a, single_select carries the flag too (the "no clear winner" opt-out
// sitting alongside the tested items), so it is preserved here, through a type
// change from multi_select as well: an escape stays an escape, as the server's
// applier keeps it. It never throws — it heals the shape — in line with
// "log/repair, don't block unless unusable".
// derivation: S71 (the manual editor's canonical response shape; the same
// escape rule as the applier's S60 and stamping's S21).
function canonicalizeResponse(
  response: ResponseDefinition,
): ResponseDefinition {
  const r = response as any;
  if (r.kind === "multi_select" && Array.isArray(r.options)) {
    const out: any = { ...r, options: sortPinnedLast(r.options, r.givenOrder) };
    return { ...out, ...selectionCountsHeld(out) };
  }
  if (r.kind === "single_select" && Array.isArray(r.options)) {
    const sorted = sortPinnedLast(r.options, r.givenOrder);
    return sorted === r.options ? response : { ...r, options: sorted };
  }
  if (r.kind === "ranking" && Array.isArray(r.options)) {
    const out: any = {
      ...r,
      options: r.options.some((option: any) => option?.exclusive || option?.keepLast)
        ? r.options.map((option: any) => {
            const { exclusive: _drop, keepLast: _dropKeepLast, ...rest } = option;
            return rest;
          })
        : r.options,
    };
    return { ...out, ...rankingCountsHeld(out) };
  }
  return response;
}

/**
 * A typed roster (`optionSource`, plan 09 S3.3) offers two kinds of answer.
 * Its asset answers are the tested assets of one family, each standing for
 * its asset by a typed `choice` ref: the server adds and removes them as
 * assets come and go, and one is renamed through its asset, never in the
 * question. Its other answers (the opt-outs, a literal answer just typed in)
 * are the question's own. Whether an edit keeps the roster is the server's to
 * decide at Save (the operations applier's `settleRosterLink`): the editor
 * keeps the mark it was given and never sends it.
 */
function isRosterAnswer(response: unknown, item: unknown): boolean {
  const source = (response as { optionSource?: unknown } | null)?.optionSource;
  return (
    typeof source === "object" &&
    source !== null &&
    typeof item === "object" &&
    item !== null &&
    (item as { exclusive?: unknown }).exclusive !== true &&
    ((item as ChoiceOption).assetRefs ?? []).some(
      (ref) => ref.role === "choice",
    )
  );
}

/** An option row as the editor hands it back; a new row has no id/value yet. */
type ChoiceOptionEdit = ItemEdit<ChoiceOption>;
type MatrixRowEdit = ItemEdit<MatrixRow>;
type MatrixMeasureEdit = Partial<Omit<MatrixRatingMeasure, "id">> & {
  id?: string;
  label: string;
};

// Writes the option list exactly as the editor renders it: every row in order,
// blank labels included. Edit, remove and add each pass the option objects
// themselves, so identity (id, value, description/exclusive/assetRefs) travels
// with the row and never with a matching label (F16: a blank row made a
// filtered-label index hit the next real option). Survivors keep their id and
// value while still free; new rows and duplicates get fresh ones.
function responseWithChoiceOptions<Q extends RespondentQuestion>(
  question: Q,
  options: Array<ChoiceOptionEdit>,
): Q {
  const response = question.response as any;
  if (
    response.kind !== "single_select" &&
    response.kind !== "multi_select" &&
    response.kind !== "ranking"
  ) {
    return question;
  }
  const takenValues = new Set<string>();
  const takenIds = new Set<string>();
  const updatedOptions: Array<ChoiceOption> = options.map((option) => ({
    ...option,
    id: keepOrMint(
      option.id,
      "", // an id is the code's, never read from a label (FE11)
      `${question.stableKey}_opt`,
      takenIds,
    ),
    value: keepOrMint(
      option.value,
      option.label,
      `${question.stableKey}_opt`,
      takenValues,
    ),
  }));
  const nextResponse: any = { ...response, options: updatedOptions };
  // QA2 C1. `...response` above already carries whatever `randomizeOptions`
  // value the CURRENT response has, present or absent, through unchanged, so
  // fixing a typo in one option never disarms randomization. An option edit
  // does not write the key (absent means false, as the normaliser stores
  // it). Ranking never carries this key
  // (runtime.ts:896-905 rejects a truthy value there), so strip it
  // defensively even if it somehow arrived on the incoming response.
  if (nextResponse.kind === "ranking") {
    delete nextResponse.randomizeOptions;
  }
  return {
    ...question,
    response: canonicalizeResponse(nextResponse),
  };
}

function responseWithRandomizeOptions<Q extends RespondentQuestion>(
  question: Q,
  randomizeOptions: boolean,
): Q {
  // The marketer toggling this pill is a stated intent, so unlike the
  // preserve-on-edit tri-state C1/C2 protect (absent stays absent through an
  // unrelated edit), a direct toggle always writes an explicit boolean.
  // A grid's rows shuffle through `randomizeRows`. Otherwise single_select
  // and multi_select only: ranking rejects the key outright
  // (runtime.ts:896-905) and no other response kind has option order to
  // control.
  const response = question.response as any;
  if (MATRIX_QUESTION_TYPES.has(response.kind)) {
    return {
      ...question,
      response: canonicalizeResponse({
        ...response,
        randomizeRows: randomizeOptions,
      }),
    };
  }
  if (response.kind !== "single_select" && response.kind !== "multi_select") {
    return question;
  }
  // Plots plan C3: an ordered list is never shuffled (the Save refuses it as
  // plan_ordered_randomized), so shuffling one on is a no-op; off stays open.
  if (randomizeOptions && response.ordered === true) {
    return question;
  }
  return {
    ...question,
    response: canonicalizeResponse({
      ...response,
      randomizeOptions,
    }),
  };
}

function responseWithChoiceExclusive<Q extends RespondentQuestion>(
  question: Q,
  index: number,
  exclusive: boolean,
): Q {
  // RND-2599: mark/unmark a single multi-select option as a mutually-exclusive
  // opt-out (e.g. "none of these"). A single_select opt-out is planner and
  // AUTHOR authored, not a marketer-facing UI affordance, so this editor
  // stays multi_select only; ranking has no exclusive concept.
  const response = question.response as any;
  if (response.kind !== "multi_select") {
    return question;
  }
  const options = (response.options ?? []) as Array<ChoiceOption>;
  if (index < 0 || index >= options.length) {
    return question;
  }
  const updatedOptions = options.map((option, optionIndex) => {
    if (optionIndex !== index) {
      return option;
    }
    if (exclusive) {
      return { ...option, exclusive: true };
    }
    const { exclusive: _removed, ...rest } = option;
    return rest;
  });
  return {
    ...question,
    // Routed through canonicalizeResponse (QA2 A3) rather than returned
    // directly: this op sets the flag at whatever index the option already
    // occupies, so without the sort it is the most direct UI path to a
    // mid-list exclusive.
    response: canonicalizeResponse({ ...response, options: updatedOptions }),
  };
}

// Marks or unmarks one single or multi select option as a catch-all
// (`ChoiceOption.keepLast`, D12): it stays last when the list is shuffled, and
// the report never counts it as a competing answer. It keeps every other
// field and moves to the pinned tail, as an escape does (`canonicalizeResponse`),
// so the order shown with randomization off is the order respondents see.
function responseWithChoiceKeepLast<Q extends RespondentQuestion>(
  question: Q,
  index: number,
  keepLast: boolean,
): Q {
  const response = question.response as any;
  if (response.kind !== "single_select" && response.kind !== "multi_select") {
    return question;
  }
  const options = (response.options ?? []) as Array<ChoiceOption>;
  if (index < 0 || index >= options.length) {
    return question;
  }
  const updatedOptions = options.map((option, optionIndex) => {
    if (optionIndex !== index) {
      return option;
    }
    if (keepLast) {
      return { ...option, keepLast: true };
    }
    const { keepLast: _removed, ...rest } = option;
    return rest;
  });
  return {
    ...question,
    response: canonicalizeResponse({ ...response, options: updatedOptions }),
  };
}

// Writes the fixed rows exactly as the editor renders them, the same way
// `responseWithChoiceOptions` writes options: each row object travels with its
// own id, value and asset refs, so a rename keeps the answer key that logic and
// analysis join on, and a removal removes that row and no other.
function responseWithRows<Q extends RespondentQuestion>(
  question: Q,
  rows: Array<MatrixRowEdit>,
): Q {
  const response = question.response as any;
  if (
    response.kind !== "matrix_rating" &&
    response.kind !== "matrix_categorical"
  ) {
    return question;
  }
  // Rows sourced from tested assets are derived from concepts, not user-edited
  // fixed labels — never silently downgrade the binding to fixed rows here.
  if (response.rows?.source === "tested_assets") {
    return question;
  }
  // Blank rows stay in place (an empty label is a transient edit state, not a
  // delete) so clearing a cell never drops the matrix below its floor; the
  // validator flags empty labels / too-few rows at launch.
  const takenValues = new Set<string>();
  const takenIds = new Set<string>();
  const nextRows: Array<MatrixRow> = rows.map((row) => {
    const id = keepOrMint(
      row.id,
      "",
      `${question.stableKey}_row`,
      takenIds,
    );
    const value = keepOrMint(row.value, row.label, id, takenValues);
    return { ...row, id, value };
  });
  return {
    ...question,
    response: {
      ...response,
      rows: {
        source: "fixed",
        rows: nextRows,
      },
    },
  };
}

// Writes a matrix's columns (rating measures or categorical categories) as the
// editor renders them, identity travelling with each item as for rows.
function responseWithColumns<Q extends RespondentQuestion>(
  question: Q,
  columns: Array<MatrixMeasureEdit | ChoiceOptionEdit>,
  language = "en",
): Q {
  const response = question.response as any;
  if (response.kind === "matrix_rating") {
    const takenIds = new Set<string>();
    const measures: Array<MatrixRatingMeasure> = (
      columns as Array<MatrixMeasureEdit>
    ).map((measure) => {
      // A measure id is a slug the normaliser re-mints on Save, so a new one
      // is minted within the slug rules: a condition written in the same Save
      // names the id the saved draft keeps.
      const id = keepOrMint(
        measure.id,
        "",
        keyWithTail(slugKey(question.stableKey), "_measure"),
        takenIds,
      );
      // A new measure starts on the default scale, anchored in the draft's
      // language; a kept one carries its own unit, anchors and N/A config.
      return {
        unit: "rating_1_5",
        anchors: defaultMeasureAnchors(language),
        includeNotApplicable: false,
        ...measure,
        id,
      } as MatrixRatingMeasure;
    });
    return {
      ...question,
      response: {
        ...response,
        measures,
      },
    };
  }
  if (response.kind === "matrix_categorical") {
    const takenValues = new Set<string>();
    const takenIds = new Set<string>();
    const categories: Array<ChoiceOption> = (
      columns as Array<ChoiceOptionEdit>
    ).map((category) => {
      const id = keepOrMint(
        category.id,
        "",
        `${question.stableKey}_cat`,
        takenIds,
      );
      const value = keepOrMint(category.value, category.label, id, takenValues);
      return { ...category, id, value };
    });
    return {
      ...question,
      response: { ...response, categories },
    };
  }
  return question;
}

// Asset refs are derived on the server from the `@mentions` a Save sends, so
// an edit changes only what the author changed.
function updateQuestionInDraft(
  draft: QuestionnaireDraft,
  questionId: string,
  updater: (question: CanonicalEditableQuestion) => CanonicalEditableQuestion,
): QuestionnaireDraft {
  return {
    ...draft,
    researchQuestions: draft.researchQuestions.map((question) =>
      question.id === questionId ? updater(question) : question,
    ),
    updatedAt: new Date().toISOString(),
  };
}

interface SaveErrorBody {
  code?: string;
  errors?: Array<{
    code?: string;
    operationIndex?: number | null;
    path?: string;
    message?: string;
    dependents?: Array<{ questionStableKey: string; path: string }>;
  }>;
}

function saveErrorBody(error: unknown): SaveErrorBody | undefined {
  return (error as { response?: { data?: SaveErrorBody } })?.response?.data;
}

/**
 * Where a refused Save points, as a question on screen: a question named by
 * stableKey (a dependent), the question the refusing operation addressed
 * (its `operationIndex` in the batch this Save sent), or a path in the draft
 * the batch would have produced, which has the on-screen order.
 */
function refusedQuestion(
  local: QuestionnaireDraft,
  operations: Array<QuestionnaireHttpOperation>,
  ref: { stableKey?: string; operationIndex?: number; resultPath?: string },
): { id: string; index: number } | null {
  let stableKey = ref.stableKey;
  if (!stableKey && typeof ref.operationIndex === "number") {
    const operation = operations[ref.operationIndex];
    stableKey =
      operation?.op === "add_question"
        ? operation.question.stableKey
        : operation &&
            "target" in operation &&
            (operation.op === "move_question" ||
              operation.op === "delete_question" ||
              operation.op === "change_type" ||
              operation.op === "edit_question")
          ? operation.target
          : undefined;
  }
  const questionId =
    stableKey === undefined && ref.resultPath !== undefined
      ? getValidationTarget(local, ref.resultPath).questionId
      : undefined;
  const index = local.researchQuestions.findIndex((question) =>
    stableKey !== undefined
      ? question.stableKey === stableKey
      : question.id === questionId,
  );
  return index >= 0
    ? { id: local.researchQuestions[index].id, index }
    : null;
}

/**
 * The toast line for a failed write to the operations route: the builder's
 * Save, and the chat's asset writes, so a refusal reads the same everywhere.
 * `draft` is the one the batch was written on, which numbers the questions
 * the refusal names; `fallback` is the caller's own line for a failure the
 * route did not answer with a refusal.
 */
export function writeFailureCopy(
  error: unknown,
  context: {
    draftId: string | null | undefined;
    draft: QuestionnaireDraft | null | undefined;
    operations?: Array<QuestionnaireHttpOperation>;
    fallback?: string;
  },
): { title: string; description: string | null } {
  const operations = context.operations ?? [];
  const draft = context.draft;
  return getSaveFailureCopy(error, {
    draftId: context.draftId,
    operations,
    questionLabel: (ref) => {
      const found = draft ? refusedQuestion(draft, operations, ref) : null;
      return found ? questionRefLabel(found.index) : null;
    },
    ...(context.fallback ? { fallback: context.fallback } : {}),
  });
}

/**
 * A refused Save's errors as validation issues on the questions they name,
 * for the cards' existing issue chip: the refusing operation's question (or
 * the result issue's), and each dependent. A batch the route could not read
 * is our bug, not a question's.
 */
function saveRefusalIssues(
  error: unknown,
  local: QuestionnaireDraft,
  operations: Array<QuestionnaireHttpOperation>,
): SaveRefusalIssues {
  const body = saveErrorBody(error);
  if (
    body?.code !== "operation_rejected" &&
    body?.code !== "operation_result_invalid"
  )
    return NO_SAVE_REFUSAL;
  const byQuestionId = new Map<string, Array<QuestionnaireValidationIssue>>();
  const byAssetId = new Map<string, Array<QuestionnaireValidationIssue>>();
  const unscoped: Array<QuestionnaireValidationIssue> = [];
  for (const entry of body.errors ?? []) {
    const code = entry?.code;
    if (typeof code !== "string") continue;
    const issue: QuestionnaireValidationIssue = {
      code,
      message: entry.message ?? "",
      path: entry.path ?? "",
      severity: "error",
    };
    const targets = [
      body.code === "operation_result_invalid"
        ? refusedQuestion(local, operations, { resultPath: issue.path })
        : refusedQuestion(local, operations, {
            operationIndex: entry.operationIndex ?? undefined,
          }),
      ...(entry.dependents ?? []).map((dependent) =>
        refusedQuestion(local, operations, {
          stableKey: dependent.questionStableKey,
        }),
      ),
    ].filter((target): target is { id: string; index: number } => !!target);
    const assetId =
      body.code === "operation_rejected"
        ? refusedAsset(local, operations, entry.operationIndex)
        : null;
    if (assetId)
      byAssetId.set(assetId, [...(byAssetId.get(assetId) ?? []), issue]);
    if (targets.length === 0 && !assetId) unscoped.push(issue);
    for (const target of new Set(targets.map((found) => found.id)))
      byQuestionId.set(target, [...(byQuestionId.get(target) ?? []), issue]);
  }
  return { byQuestionId, byAssetId, unscoped };
}

function buildQuestionRecords(
  draft: QuestionnaireDraft | null,
): Array<QuestionnaireQuestionRecord> {
  if (!draft) {
    return [];
  }
  return [
    ...draft.researchQuestions.map((question, index) => ({
      id: question.id,
      index,
      question,
      sectionLabel: "Research",
    })),
  ];
}

/** The draft's question and asset keys: one namespace, compared case aside. */
function draftKeys(draft: QuestionnaireDraft): Set<string> {
  return takenKeys([
    ...draft.researchQuestions.map((question) => question.stableKey),
    ...(draft.testedAssets?.assets ?? []).map((asset) => asset.stableKey),
  ]);
}

function buildDefaultResearchQuestion(
  draft: QuestionnaireDraft,
): QuestionDefinition {
  const stableKey = mintKey(
    [`new_question_${draft.researchQuestions.length + 1}`],
    draftKeys(draft),
    "question",
  );
  // Blank, shown with placeholders: typing never appends to seeded text. Saving
  // it untouched is refused, and the refusal names the blank field on the card.
  return {
    id: `q_${stableKey}`,
    stableKey,
    type: "single_select",
    role: "research",
    title: "",
    required: true,
    response: {
      kind: "single_select",
      options: choiceOptions(stableKey, ["", ""]),
      randomizeOptions: false,
    },
    metricRefs: [`metric_${stableKey}`],
    slideRefs: [`slide_${stableKey}`],
    tags: [],
    createdBy: "user",
    updatedAt: new Date().toISOString(),
  };
}

// The supported scales whose points take labels, by point count
// (`config/questionnaire-rating-scales.json`).
const LABELED_RATING_UNITS: Readonly<Record<number, RatingUnitId>> =
  Object.fromEntries(
    POINT_LABEL_RATING_UNIT_IDS.map((unit) => [RATING_SCALE_POINTS[unit], unit]),
  );

/** The middle position of a `span`-point label list (the lower middle when even). */
export function midpointIndex(span: number): number {
  return Math.floor((span - 1) / 2);
}

function scaleLabelsFromOptions(
  options: Array<ChoiceOption>,
): Array<string> {
  return options
    .filter((option) => !(option as { exclusive?: boolean }).exclusive)
    .map((option) => option.label)
    .filter((label) => typeof label === "string" && label.trim().length > 0);
}

function ratingFromScaleLabels(
  labels: Array<string>,
): RatingResponse | null {
  const unit = LABELED_RATING_UNITS[labels.length];
  if (!unit) {
    return null;
  }
  return {
    kind: "rating",
    unit,
    pointLabels: labels,
    anchors: {
      minLabel: labels[0],
      midpointLabel: ratingMidpointLabel(
        unit,
        labels[midpointIndex(labels.length)],
      ),
      maxLabel: labels[labels.length - 1],
    },
    includeNotApplicable: false,
  };
}

/** A worded scale's point labels, or null for a numeric scale. */
function wordedScalePoints(
  pointLabels: Array<string> | undefined,
): Array<string> | null {
  if (
    !Array.isArray(pointLabels) ||
    pointLabels.length < 2 ||
    pointLabels.some((label) => typeof label !== "string")
  ) {
    return null;
  }
  return pointLabels;
}

/**
 * A rating grid's one worded scale as choice-grid categories (RND-2916), the
 * way a rating's worded points become options (`carriedAnswers`).
 * A grid has one scale when every measure words the same points; measures on
 * different scales have no single set of categories, so that grid takes the
 * choice grid's blank seed.
 */
function categoriesFromMeasureScale(
  stableKey: string,
  measures: Array<{ pointLabels?: Array<string> }> | undefined,
): Array<ChoiceOption> | null {
  if (!Array.isArray(measures) || measures.length === 0) {
    return null;
  }
  const points = wordedScalePoints(measures[0].pointLabels);
  if (
    !points ||
    measures.some(
      (measure) =>
        !Array.isArray(measure.pointLabels) ||
        measure.pointLabels.length !== points.length ||
        measure.pointLabels.some((label, index) => label !== points[index]),
    )
  ) {
    return null;
  }
  return choiceOptions(`${stableKey}_cat`, points);
}

function carryOverResponse(
  question: CanonicalEditableQuestion,
  nextType: QuestionDefinition["type"],
  stableKey: string,
  remembered?: ResponseDefinition,
  language = "en",
): ResponseDefinition {
  const prevResponse = question.response as ResponseDefinition;
  const prevKind = prevResponse.kind;

  // If we previously held a response of EXACTLY this kind (the user is switching
  // back), restore it so custom scales / measures / options survive the round
  // trip instead of collapsing to defaults. For matrix kinds, overlay the rows
  // currently on the question so row edits made in the interim are not lost.
  if (remembered && (remembered as any).kind === nextType) {
    if (MATRIX_QUESTION_TYPES.has(nextType)) {
      const currentRows = (prevResponse as any).rows;
      return {
        ...(remembered as any),
        ...(currentRows ? { rows: currentRows } : {}),
      };
    }
    return remembered;
  }

  // One rule for every pair of types (E-questions-11): the new type takes the
  // question's answers where it has a place for them (`ANSWERS_TAKEN_FROM`)
  // and a grid's rows when it is a grid, and its own seed for the rest.
  const prevAny = prevResponse as any;
  const answers = ANSWERS_TAKEN_FROM[nextType]?.has(prevKind)
    ? carriedAnswers(question, stableKey)
    : null;
  const prevRows = MATRIX_QUESTION_TYPES.has(prevKind) ? prevAny.rows : undefined;

  // With no answers to take, a list is the new type's seed (a ranking with
  // no stated bounds).
  if (OPTION_LIST_QUESTION_TYPES.has(nextType) && !answers) {
    return defaultResponseForType(nextType, stableKey, language);
  }
  if (OPTION_LIST_QUESTION_TYPES.has(nextType)) {
    // An escape ("None of these") stays an escape on a single or multi
    // select. A ranked list has none (D0a): nothing to rank, and on a
    // comparison an answer with no asset, which the Save refuses. It is left
    // out of a ranking; switching back restores it from the response memory.
    const carried = (answers ?? []).filter(
      (option) => nextType !== "ranking" || option.exclusive !== true,
    );
    const options =
      carried.length >= 2 ? carried : choiceOptions(stableKey, BLANK_PAIR);
    // Plots plan C3: an ordered list stays ordered across a single/multi
    // switch, and an ordered list is never shuffled. Ranking has no flag.
    const isOrdered =
      OPTION_ORDER_QUESTION_TYPES.has(prevKind) && prevAny.ordered === true;
    const carriedOrdered = isOrdered ? { ordered: true } : {};
    // The user's own list order stays theirs across a single/multi switch (`givenOrder`).
    const carriedGivenOrder =
      OPTION_ORDER_QUESTION_TYPES.has(prevKind) && prevAny.givenOrder === true
        ? { givenOrder: true }
        : {};
    if (nextType === "ranking") {
      // derivation: S72 (a type switch carries the bounds, clamped to the
      // carried options). Rank-all when switching in; a ranking keeps its
      // bounds, which `canonicalizeResponse` clamps to the options carried.
      const n = options.length;
      const keeps = (field: string) =>
        prevKind === "ranking" && typeof prevAny[field] === "number"
          ? prevAny[field]
          : n;
      return {
        kind: "ranking",
        options,
        minRankedItems: keeps("minRankedItems"),
        maxRankedItems: keeps("maxRankedItems"),
      };
    }
    // Matches B1 / the creation default: a switch into multi_select is
    // randomized, as a new one is, unless the list is ordered; a single
    // select is not.
    return {
      ...(nextType === "multi_select"
        ? { kind: "multi_select", options, minSelected: 1, randomizeOptions: !isOrdered }
        : { kind: "single_select", options, randomizeOptions: false }),
      ...carriedOrdered,
      ...carriedGivenOrder,
    } as ResponseDefinition;
  }

  if (nextType === "rating") {
    return (
      (answers ? ratingFromScaleLabels(scaleLabelsFromOptions(answers)) : null) ??
      defaultResponseForType(nextType, stableKey, language)
    );
  }

  if (MATRIX_QUESTION_TYPES.has(nextType)) {
    const seed = defaultResponseForType(nextType, stableKey, language) as any;
    const grid = {
      ...seed,
      rows: prevRows ?? seed.rows,
      randomizeRows: prevRows ? gridRowsRandomized(prevAny) : seed.randomizeRows,
    };
    if (nextType === "matrix_categorical") {
      return {
        ...grid,
        categories: answers && answers.length >= 2 ? answers : seed.categories,
      };
    }
    if (prevKind === "matrix_rating" && Array.isArray(prevAny.measures)) {
      return { ...grid, measures: prevAny.measures };
    }
    // The answers become every measure's scale when they word one.
    const scale = answers
      ? ratingFromScaleLabels(scaleLabelsFromOptions(answers))
      : null;
    return {
      ...grid,
      measures: scale
        ? seed.measures.map((measure: any) => ({
            ...measure,
            unit: scale.unit,
            pointLabels: scale.pointLabels,
            anchors: scale.anchors,
          }))
        : seed.measures,
    };
  }

  return defaultResponseForType(nextType, stableKey, language);
}

/**
 * Which types' answers a type takes on a type change (E-questions-11). A
 * scale's points (a rating's, a rating grid's one scale) are a scale's: a
 * single select or a choice grid can ask them, a multi-select or a ranking
 * cannot. A list of things (a multi-select's, a ranking's) is a list's: a
 * single select or a choice grid can offer it, a rating cannot. A single
 * select's options and a choice grid's categories can be either.
 */
const SCALE_ANSWERS: ReadonlySet<string> = new Set([
  "rating",
  "matrix_rating",
  "single_select",
  "matrix_categorical",
]);
const LIST_ANSWERS: ReadonlySet<string> = new Set([
  "single_select",
  "multi_select",
  "ranking",
  "matrix_categorical",
]);
const ANSWERS_TAKEN_FROM: Readonly<Record<string, ReadonlySet<string>>> = {
  rating: SCALE_ANSWERS,
  matrix_rating: SCALE_ANSWERS,
  multi_select: LIST_ANSWERS,
  ranking: LIST_ANSWERS,
  single_select: new Set([...SCALE_ANSWERS, ...LIST_ANSWERS]),
  matrix_categorical: new Set([...SCALE_ANSWERS, ...LIST_ANSWERS]),
};

/**
 * The question's answers as an option list, for the type it changes to: its
 * options, a choice grid's categories, a rating's worded points or a rating
 * grid's one worded scale (`categoriesFromMeasureScale`). Null when it has
 * none (a number, an open end, a numeric or mixed scale).
 */
function carriedAnswers(
  question: CanonicalEditableQuestion,
  stableKey: string,
): Array<ChoiceOption> | null {
  const response = question.response as any;
  if (OPTION_LIST_QUESTION_TYPES.has(response.kind)) {
    return extractChoiceOptions(question);
  }
  if (response.kind === "matrix_categorical" && Array.isArray(response.categories)) {
    return response.categories as Array<ChoiceOption>;
  }
  if (response.kind === "rating") {
    const points = wordedScalePoints((response as RatingResponse).pointLabels);
    return points ? choiceOptions(stableKey, points) : null;
  }
  if (response.kind === "matrix_rating") {
    return categoriesFromMeasureScale(stableKey, response.measures);
  }
  return null;
}

function normalizeTypeForQuestion(
  question: CanonicalEditableQuestion,
  nextType: string,
  remembered?: Partial<Record<string, ResponseDefinition>>,
  language = "en",
): CanonicalEditableQuestion {
  const allowedType = QUESTIONNAIRE_RESEARCH_TYPE_OPTIONS.some(
    (option) => option.id === nextType,
  )
    ? (nextType as QuestionDefinition["type"])
    : "single_select";
  const stableKey = question.stableKey;
  return {
    ...question,
    type: allowedType,
    response: canonicalizeResponse(
      carryOverResponse(
        question,
        allowedType,
        stableKey,
        remembered?.[allowedType],
        language,
      ),
    ),
    metricRefs:
      allowedType === "open_end"
        ? [`qual_${stableKey}`]
        : [`metric_${stableKey}`],
    slideRefs:
      allowedType === "open_end"
        ? [`slide_${stableKey}_positive`, `slide_${stableKey}_negative`]
        : [`slide_${stableKey}`],
    updatedAt: new Date().toISOString(),
  };
}

export function getTypeOption(
  question: Pick<CanonicalEditableQuestion, "type">,
): QuestionnaireTypeOption {
  return (
    QUESTIONNAIRE_RESEARCH_TYPE_OPTIONS.find(
      (option) => option.id === question.type,
    ) || {
      id: "open_end",
      label: "Question",
      badgeLabel: "Question",
      tone: "gray",
    }
  );
}

export function getTypeOptions(
  _question: CanonicalEditableQuestion,
): Array<QuestionnaireTypeOption> {
  return QUESTIONNAIRE_RESEARCH_TYPE_OPTIONS;
}

export function useQuestionnaireWorkspace(
  options: UseQuestionnaireWorkspaceOptions,
): QuestionnaireWorkspaceState {
  const {
    assumptions: _assumptions,
    onSaveDraft,
    onValidateDraft,
    selectedPopulation,
    serverDraft,
    serverRevision,
    serverSurveyJson,
  } = options;

  const [workspaceDraft, setWorkspaceDraft] =
    useState<QuestionnaireDraft | null>(null);
  const [workspaceSurveyJson, setWorkspaceSurveyJson] =
    useState<SurveyJson | null>(null);
  // The newest accepted draft (`savedSnapshot`, at `acceptedRevisionRef`) and
  // the draft the unsaved edits began from (`editBase`), the base Save diffs
  // against. They are one draft except while edits outlive a newer accepted
  // draft (a chat turn, another tab, the draft a 409 handed back, the result
  // of a Save typed through): the edits stay as they are and the next Save
  // sends them at the newest revision, so the applier puts them on that
  // draft and says what became of each (L12). Mirrored in refs so a Save from
  // a stale closure still pairs them with `acceptedRevisionRef`.
  const [savedSnapshot, setSavedSnapshotState] = useState<string | null>(null);
  const [editBase, setEditBaseState] = useState<string | null>(null);
  const savedSnapshotRef = useRef<string | null>(null);
  const editBaseRef = useRef<string | null>(null);
  const setBaseline = useCallback(
    (saved: string | null, base: string | null) => {
      savedSnapshotRef.current = saved;
      editBaseRef.current = base;
      setSavedSnapshotState(saved);
      setEditBaseState(base);
    },
    [],
  );
  // A refusal belongs to the draft it refused, by content fingerprint (L3).
  const [saveRefusal, setSaveRefusal] = useState<{
    fingerprint: string | null;
    issues: SaveRefusalIssues;
  }>(NO_REFUSAL_STATE);
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [assetUploadCount, setAssetUploadCount] = useState(0);
  const workspaceGenerationRef = useRef(0);
  const workspaceMountedRef = useRef(true);
  const activeAssetUploadsRef = useRef(new Set<symbol>());
  const latestAssetUploadRef = useRef(new Map<string, symbol>());
  // The Save in flight: its token, and the outcome a second ask waits for.
  const activeSaveRef = useRef<{
    token: symbol;
    outcome: Promise<WorkspaceSaveOutcome>;
  } | null>(null);

  useEffect(() => {
    // Both refs hold one collection for the hook's lifetime (never reassigned),
    // so the cleanup clears the same Map and Set it would read from the refs.
    const latestAssetUploads = latestAssetUploadRef.current;
    const activeAssetUploads = activeAssetUploadsRef.current;
    workspaceMountedRef.current = true;
    workspaceGenerationRef.current += 1;
    latestAssetUploads.clear();
    activeAssetUploads.clear();
    activeSaveRef.current = null;
    setAssetUploadCount(0);
    setIsSaving(false);
    return () => {
      // Invalidate pending completions on navigation and unmount, without
      // allowing their finally blocks to release a newer draft's locks.
      workspaceMountedRef.current = false;
      workspaceGenerationRef.current += 1;
      latestAssetUploads.clear();
      activeAssetUploads.clear();
      activeSaveRef.current = null;
    };
  }, [serverDraft?.id]);

  const HISTORY_LIMIT = 50;
  const HISTORY_DEBOUNCE_MS = 500;

  // History lives in refs as the SYNCHRONOUS source of truth. State-mirrored refs
  // lag within a synchronous op sequence and corrupt redo / strand undo; refs make
  // every transition atomic. `presentJsonRef` is the current draft JSON; a tick
  // state forces re-render so canUndo/canRedo stay live.
  const historyRef = useRef<{ past: Array<string>; future: Array<string> }>({
    past: [],
    future: [],
  });
  const presentJsonRef = useRef<string | null>(null);
  // The last serverDraft INPUT we reacted to. Lets the serverDraft effect tell a
  // genuinely new push apart from the same prop being re-passed after a local save
  // (which must NOT clobber the just-saved edit — the CRITICAL data-loss bug).
  const adoptedServerSnapshotRef = useRef<string | null>(null);
  const lastDebouncedPushAtRef = useRef<number>(0);
  // What the last debounced frame was for: typing in one field coalesces into
  // one Undo step, and an edit to anything else starts its own step at once,
  // however soon it follows (E-questions-17, E-session-12).
  const lastDebouncedTargetRef = useRef<string | null>(null);
  const [, setHistoryTick] = useState(0);
  const bumpHistory = useCallback(() => setHistoryTick((tick) => tick + 1), []);
  // Bumped on every local commit; used to avoid clobbering mid-flight saves.
  const localDraftRevisionRef = useRef(0);
  // A Save the draft moved under (409): its line, said once by the adoption
  // of the draft it handed back (`announceSaveConflict`), never beside a
  // second one.
  const pendingSaveConflictRef = useRef<{
    title: string;
    description: string | null;
  } | null>(null);
  const acceptedRevisionRef = useRef<number | undefined>(serverRevision);
  const draftRef = useRef<QuestionnaireDraft | null>(null);

  // Per-question memory of the last response seen for each kind, so switching a
  // question's type away and back restores its custom config (scale, measures,
  // options) instead of collapsing to defaults. Ephemeral (never persisted).
  const responseMemoryRef = useRef<
    Record<string, Partial<Record<string, ResponseDefinition>>>
  >({});

  useEffect(() => {
    draftRef.current = workspaceDraft;
  }, [workspaceDraft]);

  // Keep the synchronous "present" pointer (and draftRef) in lock-step with
  // whatever becomes the working draft, so the next history push captures truth.
  const trackPresent = useCallback((draft: QuestionnaireDraft | null) => {
    presentJsonRef.current = draft ? JSON.stringify(draft) : null;
    draftRef.current = draft;
  }, []);

  const resetHistory = useCallback(() => {
    historyRef.current = { past: [], future: [] };
    lastDebouncedPushAtRef.current = 0;
    lastDebouncedTargetRef.current = null;
    bumpHistory();
  }, [bumpHistory]);

  /**
   * Push the draft as it is before an edit, as one Undo step. "now" always
   * pushes. "debounced" (a keystroke) pushes unless the last push was a
   * debounced one for the same `target` (the question, asset or survey field
   * being typed in) less than `HISTORY_DEBOUNCE_MS` ago.
   */
  const recordCurrentSnapshot = useCallback(
    (mode: "now" | "debounced" = "debounced", target: string | null = null) => {
      const current =
        presentJsonRef.current ??
        (draftRef.current ? JSON.stringify(draftRef.current) : null);
      if (!current) {
        return;
      }
      const now = Date.now();
      if (
        mode === "debounced" &&
        target === lastDebouncedTargetRef.current &&
        now - lastDebouncedPushAtRef.current < HISTORY_DEBOUNCE_MS
      ) {
        return;
      }
      const { past } = historyRef.current;
      if (past.length === 0 || past[past.length - 1] !== current) {
        const nextPast = [...past, current];
        historyRef.current = {
          past:
            nextPast.length > HISTORY_LIMIT
              ? nextPast.slice(nextPast.length - HISTORY_LIMIT)
              : nextPast,
          future: [],
        };
        bumpHistory();
      }
      lastDebouncedPushAtRef.current = mode === "debounced" ? now : 0;
      lastDebouncedTargetRef.current = mode === "debounced" ? target : null;
    },
    [bumpHistory],
  );

  const savedDraft = useMemo(
    () =>
      savedSnapshot ? (JSON.parse(savedSnapshot) as QuestionnaireDraft) : null,
    [savedSnapshot],
  );
  const editBaseDraft = useMemo(
    () => (editBase ? (JSON.parse(editBase) as QuestionnaireDraft) : null),
    [editBase],
  );
  // Compared on content: key order and write timestamps are not edits.
  const editBaseFingerprint = useMemo(
    () => (editBaseDraft ? draftContentFingerprint(editBaseDraft) : null),
    [editBaseDraft],
  );
  const workingFingerprint = useMemo(
    () => (workspaceDraft ? draftContentFingerprint(workspaceDraft) : null),
    [workspaceDraft],
  );
  const isDirty =
    workingFingerprint !== null &&
    editBaseFingerprint !== null &&
    workingFingerprint !== editBaseFingerprint;
  // The server's dry run of the Save the draft on screen would send, once
  // edits pause: the one validity verdict (T1b), the same apply, normalize and
  // write gate the Save runs. A dry run that does not answer says nothing;
  // the Save is still the gate.
  const [dryRun, setDryRun] = useState<{
    fingerprint: string;
    refusal: SaveRefusalIssues;
    heldBack: { title: string; description: string | null } | null;
    issues: DryRunIssues;
  } | null>(null);
  useEffect(() => {
    if (!isDirty || !workspaceDraft || !editBaseDraft || !onValidateDraft || !workingFingerprint)
      return;
    let current = true;
    const timer = setTimeout(() => {
      const draftToCheck = withSavedItemValues(editBaseDraft, workspaceDraft);
      void onValidateDraft(editBaseDraft, draftToCheck, acceptedRevisionRef.current)
        .then((result) => {
          if (!current) return;
          if (!result) {
            setDryRun(null);
            return;
          }
          const operations = diffDraftToOperations(editBaseDraft, draftToCheck);
          // The refusal as the Save would receive it, read by the Save's own
          // mappers, so the chip, the tooltip and a sent Save's toast agree.
          const refused = result.refusal ? { response: { data: result.refusal } } : null;
          setDryRun({
            fingerprint: workingFingerprint,
            refusal: refused ? saveRefusalIssues(refused, draftToCheck, operations) : NO_SAVE_REFUSAL,
            heldBack: refused
              ? writeFailureCopy(refused, { draftId: draftToCheck.id, draft: draftToCheck, operations })
              : null,
            issues: dryRunIssuesByQuestionId(result, draftToCheck),
          });
        })
        .catch(() => undefined);
    }, DRY_RUN_PAUSE_MS);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [editBaseDraft, isDirty, onValidateDraft, workingFingerprint, workspaceDraft]);
  const dryRunNow =
    isDirty && dryRun !== null && dryRun.fingerprint === workingFingerprint ? dryRun : null;
  const shownSaveRefusal =
    saveRefusal.fingerprint !== null &&
    saveRefusal.fingerprint === workingFingerprint
      ? saveRefusal.issues
      : (dryRunNow?.refusal ?? NO_SAVE_REFUSAL);
  const logicIssues = dryRunNow?.issues ?? NO_DRY_RUN_ISSUES;
  const saveHeldBack = dryRunNow?.heldBack ?? null;

  useEffect(() => {
    // While a Save is in flight, the drafts it produces (the save, its
    // compile) are its own result, not outside changes: saveChanges adopts
    // them when it settles, and this effect runs again then.
    if (activeSaveRef.current && serverDraft?.id === draftRef.current?.id)
      return;
    // The line of a Save the draft moved under is said by this run, which
    // adopts the draft the 409 handed back (E-session-05).
    const saveConflict = pendingSaveConflictRef.current;
    pendingSaveConflictRef.current = null;
    if (saveConflict) announceSaveConflict(saveConflict);
    if (
      serverDraft?.id === draftRef.current?.id &&
      typeof serverRevision === "number" &&
      acceptedRevisionRef.current !== undefined &&
      serverRevision < acceptedRevisionRef.current
    )
      return;
    const nextDraft = cloneDraft(serverDraft);
    if (!nextDraft) {
      trackPresent(null);
      setWorkspaceDraft(null);
      setWorkspaceSurveyJson(cloneSurveyJson(serverSurveyJson));
      setBaseline(null, null);
      adoptedServerSnapshotRef.current = null;
      resetHistory();
      return;
    }

    const nextSnapshot = JSON.stringify(nextDraft);
    // Undo history never spans a server commit (QA4 FE1). Every frame in it is
    // the edit base plus local edits, so Save diffs it against that one
    // base. A frame from before a chat turn or a save holds the server's
    // questions as they were, and Save would re-add one the turn removed as a
    // bare `add_question`, without its tags or plan attribution (the primary
    // read and its report binding). Undoing a server commit is a server revert.
    const adopt = () => {
      trackPresent(nextDraft);
      setWorkspaceDraft(nextDraft);
      setWorkspaceSurveyJson(cloneSurveyJson(serverSurveyJson));
      setBaseline(nextSnapshot, nextSnapshot);
      acceptedRevisionRef.current = serverRevision;
      adoptedServerSnapshotRef.current = nextSnapshot;
      resetHistory();
    };

    // Initial load: nothing local yet, unless this tab kept unsaved edits
    // for the draft (a reload, Back or Forward, B4). Those stay on the draft
    // they began from, as edits under a newer draft always do: the next Save
    // applies them to the draft served now, and Undo starts empty.
    if (!savedSnapshot) {
      const kept = readUnsavedDraft(nextDraft.id);
      if (!kept) {
        adopt();
        return;
      }
      if (draftContentFingerprint(kept.base) !== draftContentFingerprint(nextDraft))
        warnNewerDraft();
      trackPresent(kept.local);
      setWorkspaceDraft(kept.local);
      setWorkspaceSurveyJson(cloneSurveyJson(serverSurveyJson));
      setBaseline(nextSnapshot, JSON.stringify(kept.base));
      acceptedRevisionRef.current = serverRevision;
      adoptedServerSnapshotRef.current = nextSnapshot;
      // The refused Save's chip comes back with the edits it refused, keyed
      // by that draft's fingerprint as on the page (E-session-11).
      if (kept.refusal) setSaveRefusal(refusalFromStorage(kept.refusal));
      resetHistory();
      return;
    }

    // NOT a new push — never clobber our state. Two cases:
    //  1. The same serverDraft prop is re-passed (adoptedServerSnapshotRef match).
    //  2. The parent hands us back the just-SAVED draft (persistDraftEdits ->
    //     setDraft) — it equals savedSnapshot. Our workspace already reflects it
    //     (or carries a newer in-flight manual edit), so re-adopting would push a
    //     spurious undo frame after save cleared history AND clobber that edit.
    // Crucially this does NOT also pin adoptedServerSnapshotRef inside saveChanges:
    // the effect re-runs once with the STALE serverDraft right after save, and
    // pinning there would make that stale draft look new and get re-adopted.
    if (
      nextSnapshot === adoptedServerSnapshotRef.current ||
      nextSnapshot === savedSnapshot
    ) {
      if (nextSnapshot === savedSnapshot) {
        acceptedRevisionRef.current = serverRevision;
        // No edit is left on an older base: the workspace is the saved draft.
        if (!isDirty && editBase !== savedSnapshot) {
          adopt();
          return;
        }
      }
      adoptedServerSnapshotRef.current = nextSnapshot;
      return;
    }
    adoptedServerSnapshotRef.current = nextSnapshot;
    if (isDirty && draftRef.current && nextDraft.id === draftRef.current.id) {
      // A newer accepted draft arrived under unsaved edits (a chat turn, a
      // compile, another tab, or the draft a 409 handed back). The edits stay
      // on the draft they began from, and the next Save sends them at this
      // draft's revision: the applier puts them on it, and its effects and
      // refusals say what became of each (L12).
      if (!saveConflict) warnNewerDraft();
      setWorkspaceSurveyJson(cloneSurveyJson(serverSurveyJson));
      setBaseline(nextSnapshot, editBase);
      acceptedRevisionRef.current = serverRevision;
      return;
    }
    adopt();
  }, [
    editBase,
    isDirty,
    isSaving,
    resetHistory,
    savedSnapshot,
    serverDraft,
    serverRevision,
    serverSurveyJson,
    setBaseline,
    trackPresent,
  ]);

  // The unsaved edits follow the dirty state into this tab's storage (B4): kept
  // while there are any, gone once a Save or Discard leaves none.
  useEffect(() => {
    const draftId = workspaceDraft?.id;
    if (!draftId || editBase === null) return;
    if (isDirty) {
      writeUnsavedDraft(
        draftId,
        acceptedRevisionRef.current ?? null,
        editBase,
        workspaceDraft,
        refusalForStorage(saveRefusal),
      );
    } else {
      clearUnsavedDraft(draftId);
    }
  }, [editBase, isDirty, saveRefusal, workspaceDraft]);

  const questions = useMemo(
    () => buildQuestionRecords(workspaceDraft),
    [workspaceDraft],
  );
  const conceptAssets = useMemo(
    () => getConceptAssets(workspaceDraft?.testedAssets),
    [workspaceDraft?.testedAssets],
  );
  // A mention field binds against the saved catalog first, then the pending
  // one, as the Save does (C23-f). One table per catalog, built when it changes.
  const savedMentionTable = useMemo(
    () =>
      buildAssetMentionBindingTable(
        savedSnapshot === null
          ? undefined
          : (JSON.parse(savedSnapshot) as QuestionnaireDraft).testedAssets,
      ),
    [savedSnapshot],
  );
  const liveMentionTable = useMemo(
    () => buildAssetMentionBindingTable({ assets: conceptAssets }),
    [conceptAssets],
  );
  const mentionTables = useMemo(
    () => ({ saved: savedMentionTable, live: liveMentionTable }),
    [liveMentionTable, savedMentionTable],
  );
  // The card's spinner (D2): a Save that carries a new video watches it, so
  // while that Save runs the card of each video not saved yet says so.
  const analyzingConceptIds = useMemo(() => {
    if (!isSaving || savedSnapshot === null) return NO_CONCEPTS_ANALYZING;
    const saved = JSON.parse(savedSnapshot) as QuestionnaireDraft;
    const savedVideos = new Set(
      getConceptAssets(saved.testedAssets).flatMap((asset) =>
        (asset.media ?? [])
          .filter((media) => media.kind === "video")
          .map((media) => media.id),
      ),
    );
    return new Set(
      conceptAssets
        .filter((asset) =>
          (asset.media ?? []).some(
            (media) => media.kind === "video" && !savedVideos.has(media.id),
          ),
        )
        .map((asset) => asset.id),
    );
  }, [conceptAssets, isSaving, savedSnapshot]);

  useEffect(() => {
    if (questions.length === 0) {
      setSelectedQuestionId(null);
      return;
    }

    const hasSelectedQuestion = selectedQuestionId
      ? questions.some((question) => question.id === selectedQuestionId)
      : false;

    if (!hasSelectedQuestion) {
      setSelectedQuestionId(questions[0].id);
    }
  }, [questions, selectedQuestionId]);

  const updateWorkspaceDraft = useCallback(
    (updater: (draft: QuestionnaireDraft) => QuestionnaireDraft) => {
      const current = draftRef.current;
      if (!current) {
        return;
      }
      const currentJson = presentJsonRef.current ?? JSON.stringify(current);
      const next = updater(cloneDraft(current) as QuestionnaireDraft);
      const nextJson = JSON.stringify(next);
      // Compare on semantic content (ignore the volatile updatedAt timestamp): an
      // op that only re-stamps updatedAt — or is blocked at a cap/floor — is a
      // no-op, so don't churn the draft or leave a phantom history frame.
      if (
        withoutWriteTimestamps(nextJson) ===
        withoutWriteTimestamps(currentJson)
      ) {
        const { past, future } = historyRef.current;
        if (past.length > 0 && past[past.length - 1] === currentJson) {
          historyRef.current = { past: past.slice(0, -1), future };
          bumpHistory();
        }
        return;
      }
      // Commit synchronously (reuse nextJson — avoids a second full-draft stringify).
      presentJsonRef.current = nextJson;
      draftRef.current = next;
      localDraftRevisionRef.current += 1;
      setWorkspaceDraft(next);
    },
    [bumpHistory],
  );

  const getLocalDraftRevision = useCallback(
    () => localDraftRevisionRef.current,
    [],
  );

  const saveChangesRef = useRef<() => Promise<WorkspaceSaveOutcome>>(
    async () => ({ status: "nothing_to_save", draft: null }),
  );
  const saveChanges = useCallback(
    async (): Promise<WorkspaceSaveOutcome> => {
      // One Save at a time. A second ask (a chat send while the panel's Save
      // runs) waits for the one in flight, then saves whatever it left
      // unsaved, instead of returning with nothing said (RND-2923).
      const inFlight = activeSaveRef.current;
      if (inFlight) {
        const joined = await inFlight.outcome;
        const left = draftRef.current;
        const base = editBaseRef.current;
        if (
          joined.status !== "saved" ||
          !left ||
          !base ||
          draftContentFingerprint(left) ===
            draftContentFingerprint(JSON.parse(base))
        )
          return joined;
        return saveChangesRef.current();
      }
      // Read from draftRef (not the closed-over workspaceDraft state) so that
      // callers who synchronously chain multiple updateQuestion(...) calls
      // followed immediately by saveChanges() persist the latest edits rather
      // than the stale draft captured when this callback was created.
      const edited = draftRef.current;
      const baseSnapshot = editBaseRef.current;
      if (!edited || !baseSnapshot) {
        const saved = savedSnapshotRef.current;
        return {
          status: "nothing_to_save",
          draft: saved ? (JSON.parse(saved) as QuestionnaireDraft) : null,
        };
      }
      const baseDraft = JSON.parse(baseSnapshot) as QuestionnaireDraft;
      // No client rule holds the Save back: the server's dry run of this Save
      // does (`saveHeldBack`), and the operations gate refuses what is broken.
      // This Save fixes each new item's value from the label typed into it;
      // the workspace holds that draft, so what is sent is what is on screen.
      const draftToSave = withSavedItemValues(baseDraft, edited);
      if (draftToSave !== edited) updateWorkspaceDraft(() => draftToSave);
      const revisionAtStart = localDraftRevisionRef.current;
      const generation = workspaceGenerationRef.current;
      const saveToken = Symbol("save");
      const ownsSave = () =>
        workspaceMountedRef.current &&
        workspaceGenerationRef.current === generation &&
        draftRef.current?.id === draftToSave.id &&
        activeSaveRef.current?.token === saveToken;
      const run = async (): Promise<WorkspaceSaveOutcome> => {
        setIsSaving(true);
        setSaveRefusal(NO_REFUSAL_STATE);
        try {
          const result = await onSaveDraft(
            baseDraft,
            draftToSave,
            acceptedRevisionRef.current,
          );
          if (!ownsSave()) return SUPERSEDED;
          if (
            typeof result?.revision === "number" &&
            (acceptedRevisionRef.current === undefined ||
              result.revision > acceptedRevisionRef.current)
          )
            acceptedRevisionRef.current = result.revision;
          const savedDraft = cloneDraft(result?.draft ?? draftToSave);
          const savedJson = JSON.stringify(savedDraft);
          const saved: WorkspaceSaved = {
            ...result,
            status: "saved",
            draft: savedDraft as QuestionnaireDraft,
            surveyJson: result?.surveyJson ?? null,
          };
          announceSaveEffects(result?.effects);
          // Edits typed while the Save ran stay on the draft it sent: the next
          // Save applies them to what this one produced (L12). The history
          // goes with the old base, as on every adoption.
          if (
            localDraftRevisionRef.current !== revisionAtStart ||
            draftRef.current !== draftToSave
          ) {
            setBaseline(savedJson, JSON.stringify(draftToSave));
            resetHistory();
            if (result?.surveyJson) {
              setWorkspaceSurveyJson(cloneSurveyJson(result.surveyJson));
            }
            return saved;
          }
          trackPresent(savedDraft);
          setWorkspaceDraft(savedDraft);
          setWorkspaceSurveyJson(cloneSurveyJson(result?.surveyJson ?? null));
          setBaseline(savedJson, savedJson);
          resetHistory();
          // No success toast: the Save control itself clears when the edits are
          // saved. Failures are surfaced below.
          return saved;
        } catch (error) {
          if (!ownsSave()) return SUPERSEDED;
          console.error("Failed to save questionnaire draft:", error);
          // One mapper, one toast. A refusal names the questions it would break;
          // they carry the same line on their cards while the draft is the one
          // it refused. The refusing operation is found by its index in the
          // batch this Save sent (the same pure diff of the same two drafts).
          const operations = diffDraftToOperations(baseDraft, draftToSave);
          const copy = writeFailureCopy(error, {
            draftId: draftToSave.id,
            draft: draftToSave,
            operations,
          });
          // A 409 is said once, by the adoption of the draft it handed back
          // (E-session-05).
          if (isQuestionnaireDraftConflict(error)) {
            pendingSaveConflictRef.current = copy;
          } else {
            toast.error(copy.title, {
              description: copy.description ?? undefined,
              position: SAVE_TOAST_POSITION,
              ...signInActionFor(error),
            });
          }
          const issues = saveRefusalIssues(error, draftToSave, operations);
          setSaveRefusal({
            fingerprint: draftContentFingerprint(draftToSave),
            issues,
          });
          return issues === NO_SAVE_REFUSAL
            ? { status: "failed", title: copy.title, description: copy.description }
            : {
                status: "refused",
                issues,
                title: copy.title,
                description: copy.description,
              };
        } finally {
          // Only this Save's token releases the lock, whoever else moved on:
          // a lock left held would answer every later Save with nothing.
          if (activeSaveRef.current?.token === saveToken) {
            activeSaveRef.current = null;
            setIsSaving(false);
          }
        }
      };
      // The lock is held before the request starts, so even a request that
      // throws at once finds it and releases it.
      const entry = { token: saveToken, outcome: Promise.resolve(SUPERSEDED) };
      activeSaveRef.current = entry;
      entry.outcome = run();
      return entry.outcome;
    },
    [
      onSaveDraft,
      resetHistory,
      setBaseline,
      trackPresent,
      updateWorkspaceDraft,
    ],
  );
  saveChangesRef.current = saveChanges;
  const discardChanges = useCallback(() => {
    if (!savedSnapshot) {
      return;
    }
    // Discard the user's unsaved edits -> the newest saved draft.
    const baseline = JSON.parse(savedSnapshot) as QuestionnaireDraft;
    trackPresent(baseline);
    setWorkspaceDraft(baseline);
    setBaseline(savedSnapshot, savedSnapshot);
    setSaveRefusal(NO_REFUSAL_STATE);
    resetHistory();
  }, [resetHistory, savedSnapshot, setBaseline, trackPresent]);

  const updateSurveyMeta = useCallback(
    (patch: { title?: string; description?: string }) => {
      recordCurrentSnapshot(
        "debounced",
        `survey:${Object.keys(patch).sort().join(",")}`,
      );
      updateWorkspaceDraft((draft) => ({
        ...draft,
        ...(patch.title !== undefined ? { title: patch.title } : {}),
        ...(patch.description !== undefined
          ? { description: patch.description }
          : {}),
        updatedAt: new Date().toISOString(),
      }));
    },
    [recordCurrentSnapshot, updateWorkspaceDraft],
  );

  const updateQuestion = useCallback(
    (
      questionId: string,
      updater: (
        question: CanonicalEditableQuestion,
      ) => CanonicalEditableQuestion,
    ) => {
      recordCurrentSnapshot("debounced", `question:${questionId}`);
      updateWorkspaceDraft((draft) =>
        updateQuestionInDraft(draft, questionId, updater),
      );
    },
    [recordCurrentSnapshot, updateWorkspaceDraft],
  );

  const addConceptAsset = useCallback(
    // Optional explicit type: the empty-state "Add an asset" picker
    // (RND-2680) chooses the first asset's type; existing callers omit it and
    // keep the inherit-from-existing behavior.
    (explicitType?: TestedAssetType) => {
      recordCurrentSnapshot("now");
      updateWorkspaceDraft((draft) => ({
        ...draft,
        testedAssets: addConceptAssetToBlock(draft.testedAssets, explicitType),
        updatedAt: new Date().toISOString(),
      }));
    },
    [recordCurrentSnapshot, updateWorkspaceDraft],
  );

  const updateConceptAsset = useCallback(
    (conceptId: string, patch: Partial<ConceptAsset>) => {
      recordCurrentSnapshot("debounced", `asset:${conceptId}`);
      // The catalog only: a rename's @mentions and choice labels are re-pointed
      // by the server in the same Save.
      updateWorkspaceDraft((draft) => ({
        ...draft,
        testedAssets: upsertConceptAsset(draft.testedAssets, conceptId, patch),
        updatedAt: new Date().toISOString(),
      }));
    },
    [recordCurrentSnapshot, updateWorkspaceDraft],
  );

  // The panel's header type-switch re-types every asset, and this is its
  // block-level half: REPLACE the declared assetTypes. `ensureConceptAssetBlock`
  // only ever unions declared with actual, so without an explicit replace one
  // pass through a type leaves it declared forever with zero matching cards.
  const setDeclaredAssetTypes = useCallback(
    (types: Array<TestedAssetType>) => {
      recordCurrentSnapshot("now");
      updateWorkspaceDraft((draft) => {
        const block = draft.testedAssets as TestedAssetBlock | undefined;
        if (!block) {
          return draft;
        }
        return {
          ...draft,
          testedAssets: ensureConceptAssetBlock({
            ...block,
            assetTypes: types,
          }),
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [recordCurrentSnapshot, updateWorkspaceDraft],
  );

  const removeConceptAsset = useCallback(
    (conceptId: string) => {
      const uploadToken = latestAssetUploadRef.current.get(conceptId);
      latestAssetUploadRef.current.delete(conceptId);
      if (uploadToken && activeAssetUploadsRef.current.delete(uploadToken)) {
        setAssetUploadCount(activeAssetUploadsRef.current.size);
      }
      recordCurrentSnapshot("now");
      // The catalog only. What depended on the asset (its choices, rows and
      // refs) is removed by the server in the same Save and reported back;
      // a question that still names it is refused there, with its number.
      updateWorkspaceDraft((draft) => ({
        ...draft,
        testedAssets: removeConceptAssetFromBlock(draft.testedAssets, conceptId),
        updatedAt: new Date().toISOString(),
      }));
    },
    [recordCurrentSnapshot, updateWorkspaceDraft],
  );

  const attachConceptImage = useCallback(
    async (conceptId: string, file: File) => {
      const draftId = draftRef.current?.id;
      if (
        !draftId ||
        !getConceptAssets(draftRef.current!.testedAssets).some(
          (asset) => asset.id === conceptId,
        )
      )
        return;
      // The upload policy, before the bytes leave (B23): its reason, typed.
      const refusal = assetUploadRefusalLine(file);
      if (refusal) {
        toast.error(refusal);
        return;
      }
      const generation = workspaceGenerationRef.current;
      const uploadToken = Symbol("asset-upload");
      latestAssetUploadRef.current.set(conceptId, uploadToken);
      activeAssetUploadsRef.current.add(uploadToken);
      setAssetUploadCount(activeAssetUploadsRef.current.size);
      const ownsUpload = () =>
        workspaceMountedRef.current &&
        workspaceGenerationRef.current === generation &&
        draftRef.current?.id === draftId &&
        latestAssetUploadRef.current.get(conceptId) === uploadToken;
      try {
        const upload = await uploadQuestionnaireAssetFileApi(file);
        if (!ownsUpload()) return;
        const mediaRef = mediaRefFromUpload(upload, file);
        // A PDF's page 1, rendered and issued by the upload: the card's picture
        // of it. Its text is the Save's read.
        const previewMediaRefs = upload.preview
          ? [mediaRefFromPdfPreview(upload.preview, mediaRef)]
          : [];
          recordCurrentSnapshot("now");
        updateWorkspaceDraft((draft) => {
          const replacementAssets = attachConceptImageToBlock(
            draft.testedAssets,
            conceptId,
            mediaRef,
          );
          const replacementConcept = getConceptAssets(replacementAssets).find(
            (asset) => asset.id === conceptId,
          );
          const metadata = { ...(replacementConcept?.metadata ?? {}) };
          delete metadata.sceneManifest;
          return {
            ...draft,
            testedAssets: upsertConceptAsset(replacementAssets, conceptId, {
              media: [...(replacementConcept?.media ?? []), ...previewMediaRefs],
              metadata,
            }),
            updatedAt: new Date().toISOString(),
          };
        });
        // The file is stored; the draft change is an unsaved edit like any
        // other, written by Save, which reads it (a video included).
      } catch (error) {
        if (!ownsUpload()) return;
        console.error("Failed to upload concept image:", error);
        toast.error(
          requestFailureLine(error, "Failed to upload concept image"),
          signInActionFor(error),
        );
        throw error;
      } finally {
        if (activeAssetUploadsRef.current.delete(uploadToken)) {
          setAssetUploadCount(activeAssetUploadsRef.current.size);
        }
        if (latestAssetUploadRef.current.get(conceptId) === uploadToken) {
          latestAssetUploadRef.current.delete(conceptId);
        }
      }
    },
    [recordCurrentSnapshot, updateWorkspaceDraft],
  );

  // A page capture is the asset's file, and the link it was taken from is
  // the asset's link, by the collect card's one rule (`collectedAssetContent`:
  // `url`, and a landing page's `pageUrl`), E-session-14.
  const attachConceptCapture = useCallback(
    (conceptId: string, file: IssuedAssetFile, url: string) => {
      const mediaRef = mediaRefFromIssuedFile(file);
      recordCurrentSnapshot("now");
      updateWorkspaceDraft((draft) => {
        const asset = getConceptAssets(draft.testedAssets).find(
          (candidate) => candidate.id === conceptId,
        );
        if (!asset) {
          return draft;
        }
        const { media: _media, ...link } = collectedAssetContent(
          asset.type ?? "concept",
          { url: url.trim() || undefined, media: [mediaRef] },
        );
        return {
          ...draft,
          testedAssets: upsertConceptAsset(
            attachConceptImageToBlock(draft.testedAssets, conceptId, mediaRef),
            conceptId,
            link,
          ),
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [recordCurrentSnapshot, updateWorkspaceDraft],
  );

  const attachConceptUrl = useCallback(
    async (conceptId: string, url: string) => {
      const trimmed = (url ?? "").trim();
      if (!trimmed) return;
      const blocked = nonKeyframeableVideoMessage(trimmed);
      if (blocked) {
        toast.error(blocked);
        return;
      }
      try {
        // Optimistically attach a bare url_snapshot ref; the server captures the
        // page on save (addAssetMedia / materializeMissingAssetImages), promotes
        // it to a GCS image, and slices it. The reconciled draft replaces this
        // ref with the captured screenshot.
        recordCurrentSnapshot("now");
        updateWorkspaceDraft((draft) => {
          const nextAssets = attachConceptUrlToBlock(
            draft.testedAssets,
            conceptId,
            trimmed,
          );
          return {
            ...draft,
            testedAssets: nextAssets,
            updatedAt: new Date().toISOString(),
          };
        });
      } catch (error) {
        console.error("Failed to attach landing page URL:", error);
        toast.error(
          requestFailureLine(error, "Failed to attach landing page URL"),
        );
        throw error;
      }
    },
    [recordCurrentSnapshot, updateWorkspaceDraft],
  );

  const addQuestion = useCallback(() => {
    recordCurrentSnapshot("now");
    updateWorkspaceDraft((draft) => {
      if (
        draft.researchQuestions.length >=
        QUESTIONNAIRE_RESEARCH_QUESTION_CEILING
      ) {
        toast.error(
          `Canonical drafts support up to ${QUESTIONNAIRE_RESEARCH_QUESTION_CEILING} research questions.`,
        );
        return draft;
      }
      return {
        ...draft,
        researchQuestions: [
          ...draft.researchQuestions,
          buildDefaultResearchQuestion(draft),
        ],
        updatedAt: new Date().toISOString(),
      };
    });
  }, [recordCurrentSnapshot, updateWorkspaceDraft]);

  const duplicateQuestion = useCallback(
    (questionId: string) => {
      recordCurrentSnapshot("now");
      updateWorkspaceDraft((draft) => {
        if (
          draft.researchQuestions.length >=
          QUESTIONNAIRE_RESEARCH_QUESTION_CEILING
        ) {
          toast.error(
            `Canonical drafts support up to ${QUESTIONNAIRE_RESEARCH_QUESTION_CEILING} research questions.`,
          );
          return draft;
        }
        const existingKeys = draftKeys(draft);
        const nextQuestions: Array<QuestionDefinition> = [];
        draft.researchQuestions.forEach((question) => {
          nextQuestions.push(question);
          if (question.id === questionId) {
            const stableKey = mintKey(
              [`${question.stableKey}_copy`],
              existingKeys,
              "question",
            );
            nextQuestions.push({
              ...JSON.parse(JSON.stringify(question)),
              id: `q_${stableKey}`,
              stableKey,
              title: `${question.title} (copy)`,
              metricRefs:
                question.type === "open_end"
                  ? [`qual_${stableKey}`]
                  : [`metric_${stableKey}`],
              slideRefs:
                question.type === "open_end"
                  ? [
                      `slide_${stableKey}_positive`,
                      `slide_${stableKey}_negative`,
                    ]
                  : [`slide_${stableKey}`],
              createdBy: "user",
              updatedAt: new Date().toISOString(),
            });
          }
        });
        return {
          ...draft,
          researchQuestions: nextQuestions,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [recordCurrentSnapshot, updateWorkspaceDraft],
  );

  const deleteQuestion = useCallback(
    (questionId: string) => {
      recordCurrentSnapshot("now");
      updateWorkspaceDraft((draft) => {
        // WIP authoring floor: keep at least one research question so the
        // workspace never empties out. The 8-15 launch range stays enforced by
        // the backend compiler/launch path, not by this client-side floor.
        if (draft.researchQuestions.length <= 1) {
          toast.error("A questionnaire needs at least one research question.");
          return draft;
        }
        // What depends on it is cleared in the same edit (L3): the delete
        // confirm listed it, and the Save sends the cleared logic with it.
        const deleted = draft.researchQuestions.find(
          (question) => question.id === questionId,
        );
        const remaining = draft.researchQuestions.filter(
          (question) => question.id !== questionId,
        );
        return {
          ...draft,
          researchQuestions: deleted
            ? clearDependents(
                remaining,
                dependentsOfDelete(draft.researchQuestions, deleted.stableKey),
              )
            : remaining,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [recordCurrentSnapshot, updateWorkspaceDraft],
  );

  const reorderResearchQuestions = useCallback(
    (fromId: string, toId: string) => {
      if (fromId === toId) {
        return;
      }
      recordCurrentSnapshot("now");
      updateWorkspaceDraft((draft) => {
        const fromIndex = draft.researchQuestions.findIndex(
          (question) => question.id === fromId,
        );
        const toIndex = draft.researchQuestions.findIndex(
          (question) => question.id === toId,
        );
        if (fromIndex === -1 || toIndex === -1) {
          return draft;
        }
        const next = [...draft.researchQuestions];
        // Moving a question never moves a line: a line on the dragged question
        // stays in its gap, as the Save's applier keeps it.
        const exposureBoundary = exposureBoundaryBeforeMove(
          next,
          draft.exposureBoundary,
          next[fromIndex].stableKey,
        );
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        return {
          ...draft,
          researchQuestions: next,
          ...(exposureBoundary !== draft.exposureBoundary ? { exposureBoundary } : {}),
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [recordCurrentSnapshot, updateWorkspaceDraft],
  );

  /**
   * Move one of the two declared exposure marks (Phase 4b-d; decisions 10-12).
   * `null` DECLARES an empty zone (nothing is pre-exposure, or lanes run to
   * the end) rather than leaving the field absent — absent means "nobody has
   * decided yet", which a user drag can never mean again. Always `source:
   * "user"`: the agent only ever writes a mark at Build or RePlan (§2.3).
   *
   * Applies immediately (`"now"`, like `reorderResearchQuestions`) rather than
   * debounced: a mark move is one discrete drag, not a keystroke stream. The
   * new position is never corrected back if the backend later refuses it —
   * refusals surface through the same `issuesByQuestionId` / `unscopedIssues`
   * road every other structural edit uses once the draft is re-saved and
   * re-validated, so the divider is told why, not silently snapped back.
   */
  const setExposureMark = useCallback(
    (mark: QuestionnaireExposureMarkName, stableKey: string | null) => {
      recordCurrentSnapshot("now");
      updateWorkspaceDraft((draft) => {
        const existing = draft.exposureBoundary;
        const questions = draft.researchQuestions;
        const indexes = resolveExposureMarkIndexes(questions, existing);
        const revealIndex = indexes.revealIndex;
        const sharedViewIndex = indexes.sharedViewIndex;
        const movedIndex =
          stableKey === null
            ? mark === "reveal"
              ? -1
              : questions.length
            : questions.findIndex((question) => question.stableKey === stableKey);
        // One strip stands for both lines while they sit together in a
        // comparative design (`exposureLinesAreOneStrip`, the panel's own
        // rule): moving that strip moves the pair by the same number of
        // questions. Any other move changes the one line it names: a line
        // apart from the other, or the shared view's, never drags its
        // sibling (CT-10: starting the shared view at Q11 moved the exposure
        // start, unasked). The write validator still refuses a position that
        // crosses the lines.
        const view = exposureViewOf(draft);
        const moveTogether =
          mark === "reveal" &&
          view !== null &&
          exposureLinesAreOneStrip(view, indexes, questions.length);
        const nextMark: QuestionnaireExposureMark = {
          stableKey,
          source: "user",
          updatedAt: new Date().toISOString(),
        };
        // The shared view follows the strip by the same number of questions.
        const sharedViewIndexAfter = Math.max(
          -1,
          Math.min(questions.length, sharedViewIndex + movedIndex - revealIndex),
        );
        const movedSharedView =
          moveTogether && existing
            ? {
                stableKey:
                  sharedViewIndexAfter === questions.length
                    ? null
                    : questions[sharedViewIndexAfter]?.stableKey ?? null,
                source: "user" as const,
                updatedAt: nextMark.updatedAt,
              }
            : null;
        return {
          ...draft,
          exposureBoundary: {
            schemaVersion: QUESTIONNAIRE_EXPOSURE_BOUNDARY_SCHEMA_VERSION,
            reveal: mark === "reveal" ? nextMark : existing?.reveal ?? null,
            sharedView:
              mark === "sharedView"
                ? nextMark
                : movedSharedView ?? existing?.sharedView ?? null,
            [mark]: nextMark,
          },
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [recordCurrentSnapshot, updateWorkspaceDraft],
  );

  const changeQuestionType = useCallback(
    (questionId: string, nextType: string) => {
      recordCurrentSnapshot("now");
      // Stash the current response under its kind BEFORE the change (no
      // side-effects inside the state updater), so a later switch back restores it.
      const current = draftRef.current?.researchQuestions.find(
        (question) => question.id === questionId,
      );
      if (current?.response) {
        const memory = responseMemoryRef.current[questionId] ?? {};
        memory[current.response.kind] = current.response;
        responseMemoryRef.current[questionId] = memory;
      }
      updateWorkspaceDraft((draft) =>
        updateQuestionInDraft(draft, questionId, (question) =>
          normalizeTypeForQuestion(
            question,
            nextType,
            responseMemoryRef.current[questionId],
            draft.contentLanguage || "en",
          ),
        ),
      );
    },
    [recordCurrentSnapshot, updateWorkspaceDraft],
  );

  const restoreFrame = useCallback(
    (json: string) => {
      const draft = JSON.parse(json) as QuestionnaireDraft;
      trackPresent(draft);
      localDraftRevisionRef.current += 1;
      setWorkspaceDraft(draft);
      lastDebouncedPushAtRef.current = 0;
    },
    [trackPresent],
  );

  const undo = useCallback(() => {
    const { past, future } = historyRef.current;
    if (past.length === 0) {
      return;
    }
    const previous = past[past.length - 1];
    const current = presentJsonRef.current;
    const nextFuture = current ? [...future, current] : future;
    historyRef.current = {
      past: past.slice(0, -1),
      future:
        nextFuture.length > HISTORY_LIMIT
          ? nextFuture.slice(nextFuture.length - HISTORY_LIMIT)
          : nextFuture,
    };
    restoreFrame(previous);
    bumpHistory();
  }, [bumpHistory, restoreFrame]);

  const redo = useCallback(() => {
    const { past, future } = historyRef.current;
    if (future.length === 0) {
      return;
    }
    const upcoming = future[future.length - 1];
    const current = presentJsonRef.current;
    const nextPast = current ? [...past, current] : past;
    historyRef.current = {
      past:
        nextPast.length > HISTORY_LIMIT
          ? nextPast.slice(nextPast.length - HISTORY_LIMIT)
          : nextPast,
      future: future.slice(0, -1),
    };
    restoreFrame(upcoming);
    bumpHistory();
  }, [bumpHistory, restoreFrame]);

  return {
    addQuestion,
    addConceptAsset,
    analyzingConceptIds,
    assetUploadsInFlight: assetUploadCount > 0,
    attachConceptImage,
    attachConceptCapture,
    attachConceptUrl,
    changeQuestionType,
    conceptAssets,
    mentionTables,
    currentDraft: workspaceDraft,
    currentSurveyJson: workspaceSurveyJson,
    deleteQuestion,
    discardChanges,
    duplicateQuestion,
    estimatedMinutes: estimatedQuestionnaireMinutes(
      workspaceDraft?.researchQuestions.length ?? 0,
    ),
    isDirty,
    isSaving,
    questionCount: questions.length,
    questions,
    removeConceptAsset,
    setDeclaredAssetTypes,
    reorderResearchQuestions,
    setExposureMark,
    canUndo: historyRef.current.past.length > 0,
    canRedo: historyRef.current.future.length > 0,
    undo,
    redo,
    getLocalDraftRevision,
    saveChanges,
    selectedPopulation,
    selectedQuestionId,
    setSelectedQuestionId,
    savedDraft,
    surveyDescription: workspaceDraft?.description || "",
    studyObjective: workspaceDraft?.studyGoals?.decision || "",
    studyObjectiveSource: workspaceDraft?.studyGoals?.source ?? null,
    saveRefusal: shownSaveRefusal,
    logicIssues,
    saveHeldBack,
    title: workspaceDraft?.title || "Untitled questionnaire",
    updateConceptAsset,
    updateQuestion,
    updateQuestionOrderSelection: setSelectedQuestionId,
    updateSurveyMeta,
  };
}

export const canonicalEditors = {
  choiceOptions,
  extractColumns,
  extractRows,
  canonicalizeResponse,
  isRosterAnswer,
  responseWithChoiceOptions,
  responseWithChoiceExclusive,
  responseWithChoiceKeepLast,
  responseWithRandomizeOptions,
  responseWithColumns,
  responseWithRows,
  withVisibilityRule,
  withRoutingRule,
  withChoicesFrom,
};
